import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ApiError,
  type CertificadoEquip,
  type ClienteEquip,
  type DetalheSaidaEquip,
  type EquipEnsaio,
  type EquipExistente,
  type ItemSaidaEquip,
  type LeituraCertificadoEquip,
  type ResultadoGerarCodigos,
  type ResumoEquipEnsaio,
  type SaidaEquip,
} from "../api";
import { SUBABAS_EQUIP_ENSAIO } from "../config";
import { formatDate } from "../format";
import { compararNomes } from "../ordem";
import { usePodeExcluir } from "../permissoes";
import CameraScanner from "./CameraScanner";
import { chaveDoCodigo } from "../leitor-camera";
// O desenho do código de barras (Code 128) e a folha de etiquetas: o
// equipamento antigo, que nunca teve etiqueta, só passa a sair e voltar
// bipando depois de ganhar uma. Ver src/codigo-barras.ts.
import { code128SVG, htmlEtiquetas } from "../codigo-barras";
import {
  ICONS,
  IconAlert,
  IconBox,
  IconCamera,
  IconCheck,
  IconClose,
  IconDownload,
  IconEdit,
  IconFile,
  IconFlask,
  IconGauge,
  IconHistory,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSend,
  IconTrash,
  IconTruck,
} from "../icons";

/**
 * EQUIPAMENTOS DE ENSAIO — as quatro subabas, em uma tela só.
 *
 * ESTE ARQUIVO É O MESMO NO LABORATÓRIO E NA GESTÃO DA OPERAÇÃO (copiado byte
 * a byte). Por isso só importa o que os dois sistemas oferecem com o mesmo
 * nome: `api.equipamentosEnsaio`, SUBABAS_EQUIP_ENSAIO, formatDate,
 * compararNomes, usePodeExcluir, CameraScanner, chaveDoCodigo, code128SVG /
 * htmlEtiquetas e os ícones da lista acima. Mudou aqui, copie para lá.
 *
 * O cadastro é UM SÓ: a tabela `equipamentos` da Gestão da Operação (a mesma
 * que o Relatórios espelha). O Laboratório só repassa as chamadas; as travas
 * de verdade (código repetido, equipamento que já saiu, série duplicada) estão
 * no banco de lá — a tela só avisa antes, para ninguém perder tempo.
 *
 * O desenho é o do Controle de Seringas (SeringasView.tsx), com as mesmas
 * manias de bancada — leitor que não perde o foco, duplicado é aviso amarelo,
 * contador grande, teto de quantidade, câmera do celular:
 *
 *   Cadastro   os cinco dados de todo equipamento + certificado (opcional)
 *   Estoque    onde está cada um, a calibração e os certificados
 *   Saída      abre o lote (OS, cliente ou calibração externa), bipa e confirma
 *   Retorno    abre o lote que saiu, bipa o que voltou — volta "Em estoque"
 *
 * E uma regra que as seringas não precisavam: NADA BLOQUEIA por calibração,
 * condição de uso ou OS (os dados de produção não são confiáveis) — a bipagem
 * entra e mostra o ALERTA, que fica na linha até alguém olhar.
 */
interface Props {
  /** Qual subaba o menu lateral escolheu (null = painel de cartões). */
  subKey: string | null;
  onSubKeyChange: (key: string | null) => void;
  /**
   * Avisa o sistema que o CADASTRO mudou (cadastrar, editar, dar código, baixa,
   * excluir, confirmar/encerrar/apagar saída). Nunca a cada bipagem: na Gestão
   * da Operação isto recarrega quatro listas de referência.
   */
  onDataChanged: () => void;
}

type Aviso = { tipo: "ok" | "atencao" | "erro"; texto: string } | null;

/** O que a câmera recebe de volta a cada leitura (ver CameraScanner). */
type Leitura = "novo" | "duplicado" | "erro" | "alerta";

/** Onde o equipamento está (o filtro do estoque usa esta lista). */
const SITUACOES = [
  "Em estoque",
  "Em serviço",
  "Com cliente",
  "Em laboratório externo",
  "Faltante",
  "Baixado",
] as const;

/** A situação da CALIBRAÇÃO, calculada pelo servidor a partir da validade. */
const CALIBRACOES = ["Válida", "A vencer", "Vencida", "Sem validade", "Bloqueado"];

/**
 * A "condição de uso" (coluna `autorizacao`). Os valores das duas famílias
 * convivem na mesma coluna — a Gestão da Operação usa uns, o Planejamento
 * outros — e a tela não pode esconder nenhum.
 */
const CONDICOES_USO = [
  "Disponível",
  "Em atividade",
  "Em manutenção",
  "Em calibração",
  "Bloqueado",
  "Autorizado",
];
/** As condições que merecem aparecer na linha do estoque (as outras são o normal). */
const CONDICOES_DE_ATENCAO = ["Em manutenção", "Em calibração", "Bloqueado"];

/** Destino "Laboratório externo": calibração fora — volta bipando, como os outros. */
const LAB_EXTERNO = "Laboratório externo";

/** O começo dos códigos que o sistema gera (EQ-0001, EQ-0002...). */
const PREFIXO = "EQ-";

/** Períodos de calibração oferecidos (o certificado real não traz validade). */
const PERIODOS = [6, 12, 24, 36];

/** Teto do "gerar códigos" (o servidor aceita até 100 por vez). */
const MAX_GERAR = 100;

/** A câmera do formulário lê UM código: não há lista para conferir duplicado. */
const SEM_LEITURAS = new Set<string>();

/* ======================================================================
 *  PEQUENAS FUNÇÕES
 * ==================================================================== */

/**
 * A mensagem de um erro, do jeito que a pessoa precisa ler.
 *
 * Quando a Gestão da Operação (a dona dos dados) está fora, o erro chega com
 * code OPERACIONAL_DOWN / OPERACIONAL_DESATUALIZADA / INTEGRACAO_CONFIG, ou
 * REDE / RESPOSTA_INVALIDA (ver `pedirEq` em src/api.ts), e a mensagem já diz
 * o que fazer. As telas mostram essa mensagem no aviso "Tentar de novo" — e
 * NUNCA zeros ou "nenhum registro": um "0 em estoque" com o sistema fora do ar
 * faria alguém achar que sumiu tudo.
 */
function mensagemDe(err: unknown, padrao: string): string {
  return err instanceof ApiError && err.message ? err.message : padrao;
}

/** Data de hoje no formato do <input type="date"> (sem passar pelo fuso UTC). */
function hojeISO(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, para não escorregar de fuso). */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

/** Data e hora de um registro (bipado, devolvido), no fuso de quem olha. */
function dataHora(valor: string | null | undefined): string {
  if (!valor) return "—";
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return String(valor);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Só o dia de um carimbo de hora, no fuso de quem olha ("Desde"). */
function diaLocal(valor: string | null | undefined): string {
  if (!valor) return "—";
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return String(valor);
  return d.toLocaleDateString("pt-BR");
}

/** Calibração + N meses, sem escorregar de mês (31/01 + 1 mês = 28 ou 29/02). */
function somarMeses(iso: string, meses: number): string {
  const [a, m, d] = iso.split("-").map(Number);
  if (!a || !m || !d) return "";
  const base = new Date(Date.UTC(a, m - 1 + meses, d, 12));
  if (base.getUTCDate() !== d) base.setUTCDate(0);
  return base.toISOString().slice(0, 10);
}

/** Compara dois textos ignorando caixa, acento, espaço e pontuação. */
function mesmoTexto(a: unknown, b: unknown): boolean {
  const limpar = (s: unknown) =>
    String(s ?? "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  return limpar(a) === limpar(b);
}

/** "Megabras · MI 5500" — marca e modelo numa linha, sem sobrar separador. */
function marcaModelo(x: { fabricante?: string | null; modelo?: string | null }): string {
  return [x.fabricante, x.modelo].map((v) => String(v ?? "").trim()).filter(Boolean).join(" · ");
}

/** A cor da pílula de onde está — sóbria: vermelho só para a baixa. */
function tomDaSituacao(situacao: string | null | undefined): string {
  if (situacao === "Em estoque") return "pill-ok";
  if (situacao === "Baixado") return "pill-danger";
  if (situacao === "Faltante") return "pill-warn";
  return "pill-neutral";
}

/** A cor da pílula da calibração: vencida é amarelo (aviso), não alarme. */
function tomDaCalibracao(situacao: string | null | undefined): string {
  if (situacao === "Válida") return "pill-ok";
  if (situacao === "Vencida") return "pill-warn";
  return "pill-neutral";
}

/** Para onde a saída foi, em poucas palavras (lista, cabeçalho, estoque). */
function destinoCurto(x: {
  destino?: string | null;
  os_numero?: string | null;
  cliente_nome?: string | null;
  laboratorio_externo?: string | null;
}): string {
  if (x.destino === LAB_EXTERNO) {
    return `Lab. externo: ${x.laboratorio_externo || "não informado"}` +
      (x.os_numero ? ` · OS ${x.os_numero}` : "");
  }
  if (x.destino === "Cliente") return String(x.cliente_nome || "cliente não informado");
  return `OS ${x.os_numero || "não informada"}`;
}

/** Onde o equipamento está, em uma linha — vazio quando está no estoque. */
function ondeEsta(e: EquipEnsaio): string {
  if (!e.estoque_saida_id || !e.destino) return "";
  return `${destinoCurto(e)} · saída #${e.estoque_saida_id}`;
}

/** O título de um lote de saída (cabeçalho da bipagem). */
function tituloDaSaida(s: SaidaEquip, retorno: boolean): string {
  if (s.destino === LAB_EXTERNO) {
    return `${retorno ? "Retorno da calibração" : "Calibração"} / laboratório externo — ${s.laboratorio_externo ?? "não informado"}`;
  }
  if (s.destino === "Cliente") {
    return retorno
      ? `Retorno de ${s.cliente_nome ?? "cliente não informado"}`
      : `Envio para ${s.cliente_nome ?? "cliente não informado"}`;
  }
  return retorno
    ? `Retorno da OS ${s.os_numero ?? "não informada"}`
    : `Serviço Sercamp — OS ${s.os_numero ?? "não informada"}`;
}

/** Quem abriu a saída, e por qual sistema (os dois gravam no mesmo lugar). */
function quemAbriu(s: SaidaEquip): string {
  const sistema =
    s.origem_sistema === "laboratorio"
      ? "Laboratório"
      : s.origem_sistema === "operacional"
        ? "Gestão da Operação"
        : "";
  return [s.responsavel, sistema].filter(Boolean).join(" · ");
}

/** A cor da pílula do lote. */
function tomDoLote(status: string): string {
  if (status === "Concluída") return "pill-ok";
  if (status === "Enviada") return "pill-warn";
  return "pill-neutral";
}

/**
 * Escreve a folha de etiquetas numa janela JÁ ABERTA. A legenda de cada
 * etiqueta é o NOME do equipamento: é ela que evita colar a etiqueta do
 * megôhmetro no terrômetro.
 */
function escreverEtiquetas(janela: Window, codigos: string[], titulo: string, legendas: string[]) {
  janela.document.open();
  janela.document.write(htmlEtiquetas(codigos, titulo, "", legendas));
  janela.document.close();
  janela.focus();
}

/**
 * Abre a folha de etiquetas numa aba nova. A janela abre ANTES de qualquer
 * espera: aberta depois, o navegador a trata como pop-up não pedido e bloqueia.
 */
function abrirEtiquetas(
  codigos: string[],
  titulo: string,
  legendas: string[],
  onErro: (m: string) => void,
) {
  const janela = window.open("", "_blank");
  if (!janela) {
    onErro("O navegador bloqueou a janela. Libere os pop-ups para este site e tente de novo.");
    return;
  }
  escreverEtiquetas(janela, codigos, titulo, legendas);
}

/* ======================================================================
 *  A TELA
 * ==================================================================== */

export default function EquipamentosEnsaioView({ subKey, onSubKeyChange, onDataChanged }: Props) {
  const [resumo, setResumo] = useState<ResumoEquipEnsaio | null>(null);
  /** Por que os números não vieram (null = vieram, ou ainda estão vindo). */
  const [erroResumo, setErroResumo] = useState<string | null>(null);
  /** A saída aberta na tela (vale para a subaba de saída e a de retorno). */
  const [saidaAberta, setSaidaAberta] = useState<number | null>(null);

  const carregarResumo = useCallback(async () => {
    try {
      setResumo(await api.equipamentosEnsaio.resumo());
      setErroResumo(null);
    } catch (err) {
      setResumo(null);
      setErroResumo(mensagemDe(err, "Não foi possível ler os números dos equipamentos."));
    }
  }, []);

  // Os números são relidos a cada troca de subaba: quem bipou dez saídas e
  // volta ao painel quer ver o estoque de agora, não o da hora em que entrou.
  useEffect(() => {
    void carregarResumo();
  }, [carregarResumo, subKey]);

  /** O cadastro mudou: relê os números daqui e avisa o sistema. */
  const mudouCadastro = useCallback(() => {
    void carregarResumo();
    onDataChanged();
  }, [carregarResumo, onDataChanged]);

  /** Mudou algo que só os números daqui mostram (bipagem, saída nova). */
  const mudouContagem = useCallback(() => {
    void carregarResumo();
  }, [carregarResumo]);

  // Trocar de subaba fecha a saída que estava aberta: manter a de antes faria
  // a pessoa cair no Retorno já dentro de um lote que ela nem escolheu.
  useEffect(() => {
    setSaidaAberta(null);
  }, [subKey]);

  const sub = SUBABAS_EQUIP_ENSAIO.find((s) => s.key === subKey);

  return (
    <div className="crud">
      {sub && (
        <div className="an-trilha">
          <button
            className="btn-secondary btn-sm"
            onClick={() => (saidaAberta ? setSaidaAberta(null) : onSubKeyChange(null))}
          >
            ← Voltar
          </button>
          <span
            className="an-trilha-item"
            onClick={() => onSubKeyChange(null)}
            role="button"
            tabIndex={0}
          >
            Equipamentos de Ensaio
          </span>
          <span className="an-trilha-sep">›</span>
          {saidaAberta ? (
            <>
              <span
                className="an-trilha-item"
                onClick={() => setSaidaAberta(null)}
                role="button"
                tabIndex={0}
              >
                {sub.titulo}
              </span>
              <span className="an-trilha-sep">›</span>
              <strong>Saída #{saidaAberta}</strong>
            </>
          ) : (
            <strong className="an-trilha-atual">{sub.titulo}</strong>
          )}
        </div>
      )}

      {!sub && (
        <Painel
          resumo={resumo}
          erro={erroResumo}
          onTentar={() => void carregarResumo()}
          onEscolher={onSubKeyChange}
        />
      )}

      {subKey === "cadastro" && (
        <Cadastro
          resumo={resumo}
          erroResumo={erroResumo}
          onTentarResumo={() => void carregarResumo()}
          onMudou={mudouCadastro}
        />
      )}
      {subKey === "estoque" && (
        <Estoque
          resumo={resumo}
          erroResumo={erroResumo}
          onTentarResumo={() => void carregarResumo()}
          onMudou={mudouCadastro}
        />
      )}
      {subKey === "saida" && (
        <Saidas
          saidaAberta={saidaAberta}
          onAbrirSaida={setSaidaAberta}
          onCadastroMudou={mudouCadastro}
          onContagemMudou={mudouContagem}
        />
      )}
      {subKey === "retorno" && (
        <Retornos
          saidaAberta={saidaAberta}
          onAbrirSaida={setSaidaAberta}
          onCadastroMudou={mudouCadastro}
          onContagemMudou={mudouContagem}
        />
      )}
    </div>
  );
}

/**
 * O AVISO DE "INDISPONÍVEL" — um só, com o motivo que o servidor deu e o botão
 * de tentar de novo. É o que aparece no lugar de números e listas quando a
 * Gestão da Operação (a dona dos dados) não respondeu.
 */
function Indisponivel({ mensagem, onTentar }: { mensagem: string; onTentar: () => void }) {
  return (
    <div className="conf-aviso erro eq-indisponivel" role="alert">
      <span>{mensagem}</span>
      <button type="button" className="btn-secondary btn-sm" onClick={onTentar}>
        <IconRefresh size={14} /> Tentar de novo
      </button>
    </div>
  );
}

/* ======================================================================
 *  PAINEL — os quatro cartões, com o número que importa em cada um
 * ==================================================================== */

function Painel({
  resumo,
  erro,
  onTentar,
  onEscolher,
}: {
  resumo: ResumoEquipEnsaio | null;
  erro: string | null;
  onTentar: () => void;
  onEscolher: (k: string) => void;
}) {
  /*
   * Cada cartão mostra O NÚMERO DAQUELA ETAPA. Sem resumo (carregando ou fora
   * do ar) o cartão aparece sem número nenhum — nunca com zero, que mentiria.
   */
  const numeros: Record<string, { valor: number; rotulo: string }> | null = resumo
    ? {
        cadastro: { valor: resumo.estoque.total, rotulo: "equipamento(s) cadastrado(s)" },
        estoque: { valor: resumo.estoque.emEstoque, rotulo: "no estoque agora" },
        saida: { valor: resumo.saidas.emSeparacao, rotulo: "saída(s) em separação" },
        retorno: { valor: resumo.aguardandoRetorno, rotulo: "equipamento(s) para voltar" },
      }
    : null;

  return (
    <>
      <div className="dash-topo">
        <h3 className="dash-secao">O caminho do equipamento de ensaio</h3>
        <span className="muted dash-dica">
          Escolha a etapa aqui ou pelo menu, que desce dentro de “Equipamentos de Ensaio”
        </span>
      </div>

      {erro && <Indisponivel mensagem={erro} onTentar={onTentar} />}

      <div className="cards an-tipos">
        {SUBABAS_EQUIP_ENSAIO.map((s) => {
          const Icon = ICONS[s.icone] ?? IconGauge;
          const n = numeros?.[s.key];
          return (
            <button
              key={s.key}
              className={`card card-clicavel an-tipo tone-${s.tom}`}
              onClick={() => onEscolher(s.key)}
            >
              <span className="card-icon">
                <Icon size={22} />
              </span>
              <span className="card-body an-tipo-corpo">
                <span className="an-tipo-nome">
                  {s.titulo}
                  <em className={`an-selo tom-${s.tom}`}>{s.sigla}</em>
                </span>
                {n && (
                  <span className="an-tipo-numeros">
                    <strong>{n.valor}</strong> {n.rotulo}
                  </span>
                )}
                <span className="an-tipo-desc">{s.descricao}</span>
              </span>
              <span className="an-tipo-seta" aria-hidden="true">
                ›
              </span>
            </button>
          );
        })}
      </div>

      {resumo && (
        <>
          <div className="dash-topo">
            <h3 className="dash-secao">Onde estão agora</h3>
            <span className="muted dash-dica">
              {resumo.estoque.total} no cadastro — a soma das situações
            </span>
          </div>
          <div className="ser-resumo">
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.emEstoque}</strong> em estoque
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.emServico}</strong> em serviço
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.comCliente}</strong> com o cliente
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.emLabExterno}</strong> em laboratório externo
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.faltantes}</strong> faltante(s)
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.baixados}</strong> baixado(s)
            </span>
          </div>
          {/* Linha discreta, de propósito: calibração vencida é assunto do
              gestor, não motivo para pintar o painel inteiro de amarelo. */}
          <p className="muted eq-linha-discreta">
            Calibração: <strong>{resumo.calibracao.vencidas}</strong> vencida(s) ·{" "}
            <strong>{resumo.calibracao.aVencer}</strong> a vencer ·{" "}
            <strong>{resumo.calibracao.semValidade}</strong> sem validade
            {resumo.estoque.semCodigo > 0 && (
              <>
                {" · "}
                <button type="button" className="eq-link" onClick={() => onEscolher("cadastro")}>
                  {resumo.estoque.semCodigo} sem código de barras
                </button>
              </>
            )}
          </p>
        </>
      )}
    </>
  );
}

/* ======================================================================
 *  O CAMPO DO LEITOR E O CONTADOR — os mesmos da saída e do retorno
 * ==================================================================== */

/**
 * A barra de bipagem: refoca sozinha depois de cada leitura, aceita digitação
 * para quando o leitor quebra e abre a câmera do celular pelo mesmo botão.
 */
function BarraLeitor({
  campoRef,
  codigo,
  setCodigo,
  onBipar,
  ocupado,
  camAberta,
  onAbrirCamera,
  rotulo,
}: {
  campoRef: React.RefObject<HTMLInputElement>;
  codigo: string;
  setCodigo: (v: string) => void;
  onBipar: (e: React.FormEvent) => void;
  ocupado: boolean;
  camAberta: boolean;
  onAbrirCamera: () => void;
  rotulo: string;
}) {
  return (
    <form className="conf-leitor" onSubmit={onBipar}>
      <label>
        <span>{rotulo}</span>
        <input
          ref={campoRef}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder="aguardando o leitor…"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          disabled={ocupado}
        />
      </label>
      <button type="submit" className="btn-primary" disabled={ocupado || !codigo.trim()}>
        <IconCheck size={16} /> Bipar
      </button>
      {/* type="button": dentro de um <form>, o padrão de um botão é ENVIAR. */}
      {!camAberta && (
        <button type="button" className="btn-secondary" onClick={onAbrirCamera}>
          <IconCamera size={16} /> Ler pela câmera
        </button>
      )}
    </form>
  );
}

/** O contador grande (feitos × previstos) — é o número que fecha a caixa. */
function Contador({
  feito,
  previsto,
  rotuloFeito,
  rotuloPrevisto,
}: {
  feito: number;
  previsto: number;
  rotuloFeito: string;
  rotuloPrevisto: string;
}) {
  const diferenca = feito - previsto;
  const tom = previsto === 0 ? "" : diferenca === 0 ? " ok" : " alerta";
  return (
    <div className={`conf-contador${tom}`}>
      <div>
        <span className="conf-num">{feito}</span>
        <span className="conf-rot">{rotuloFeito}</span>
      </div>
      <div className="conf-sep">de</div>
      <div>
        <span className="conf-num">{previsto || "—"}</span>
        <span className="conf-rot">{rotuloPrevisto}</span>
      </div>
      {previsto > 0 && (
        <div className="conf-dif">
          {diferenca === 0
            ? "a conta fechou"
            : diferenca > 0
              ? `${diferenca} a mais`
              : `${Math.abs(diferenca)} faltando`}
        </div>
      )}
    </div>
  );
}

/* ======================================================================
 *  SUBABA 1 — CADASTRO
 * ==================================================================== */

type ModoForm = "novo" | "editar" | "dar-codigo";

/**
 * CADASTRO — o botão que abre o formulário e, embaixo, a dívida antiga: os
 * equipamentos que já existiam antes desta aba e nunca tiveram etiqueta.
 *
 * Sem código eles não saem nem voltam bipando, então a lista fica aqui, na
 * entrada, e não escondida num filtro do estoque. Dá para resolver um a um
 * ("Dar código", lendo a etiqueta que vai ser colada) ou em lote (o sistema
 * gera os códigos e a folha de etiquetas sai com o nome de cada um).
 */
function Cadastro({
  resumo,
  erroResumo,
  onTentarResumo,
  onMudou,
}: {
  resumo: ResumoEquipEnsaio | null;
  erroResumo: string | null;
  onTentarResumo: () => void;
  onMudou: () => void;
}) {
  const [form, setForm] = useState<{ modo: ModoForm; id?: number } | null>(null);
  const [semCodigo, setSemCodigo] = useState<EquipEnsaio[] | null>(null);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [marcados, setMarcados] = useState<Set<number>>(new Set());
  const [gerando, setGerando] = useState(false);
  const [aviso, setAviso] = useState<Aviso>(null);
  /** O resultado do "gerar códigos" (é dele que a folha pode ser reimpressa). */
  const [gerados, setGerados] = useState<ResultadoGerarCodigos | null>(null);
  /** Nomes dos que foram mandados para o "gerar" (os ignorados só voltam com o id). */
  const [nomesDoLote, setNomesDoLote] = useState<Record<number, string>>({});
  /** Quem já ganhou código nesta visita — o "Próximo sem código" pula esses. */
  const feitosRef = useRef<Set<number>>(new Set());

  const carregar = useCallback(async () => {
    try {
      const r = await api.equipamentosEnsaio.estoque({ semCodigo: true });
      // A→Z pelo nome (ver src/ordem.ts): é pelo nome que se acha o equipamento.
      const lista = [...r.equipamentos].sort(
        (a, b) => compararNomes(a.nome, b.nome) || compararNomes(a.numero_serie, b.numero_serie),
      );
      setSemCodigo(lista);
      setErroLista(null);
      // Quem saiu da lista (ganhou código em outra tela) sai da seleção também.
      setMarcados((m) => new Set([...m].filter((id) => lista.some((e) => e.id === id))));
    } catch (err) {
      setErroLista(mensagemDe(err, "Não foi possível ler os equipamentos sem código."));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  function alternar(id: number) {
    setMarcados((m) => {
      const novo = new Set(m);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  const lista = semCodigo ?? [];
  const todosMarcados = lista.length > 0 && lista.slice(0, MAX_GERAR).every((e) => marcados.has(e.id));

  function alternarTodos() {
    // O servidor gera até 100 por vez: "todos" marca os 100 primeiros, e a
    // pessoa gera o resto depois (a lista encolhe sozinha a cada lote).
    setMarcados(todosMarcados ? new Set() : new Set(lista.slice(0, MAX_GERAR).map((e) => e.id)));
  }

  async function gerarEImprimir() {
    const ids = lista.filter((e) => marcados.has(e.id)).map((e) => e.id);
    if (ids.length === 0) return;
    if (ids.length > MAX_GERAR) {
      setAviso({ tipo: "erro", texto: `Marque até ${MAX_GERAR} equipamentos por vez.` });
      return;
    }
    const nomes: Record<number, string> = {};
    for (const e of lista) if (marcados.has(e.id)) nomes[e.id] = e.nome;

    // A janela das etiquetas abre AGORA, dentro do clique; se abrisse depois
    // da espera, o navegador a bloquearia como pop-up.
    const janela = window.open("", "_blank");
    try {
      janela?.document.write("<p style=\"font-family:sans-serif\">Gerando os códigos…</p>");
    } catch {
      /* só o "gerando" que não aparece */
    }

    setGerando(true);
    setAviso(null);
    try {
      const r = await api.equipamentosEnsaio.gerarCodigos(ids);
      setNomesDoLote(nomes);
      setGerados(r);
      if (r.gerados.length > 0 && janela) {
        escreverEtiquetas(
          janela,
          r.gerados.map((g) => g.codigo_barras),
          `Etiquetas — ${r.gerados.length} equipamento(s) de ensaio`,
          r.gerados.map((g) => g.nome),
        );
      } else {
        janela?.close();
      }
      if (r.gerados.length > 0 && !janela) {
        setAviso({
          tipo: "atencao",
          texto:
            "Os códigos foram gerados, mas o navegador bloqueou a janela das etiquetas — use “Imprimir as etiquetas” abaixo.",
        });
      }
      setMarcados(new Set());
      await carregar();
      if (r.gerados.length > 0) onMudou();
    } catch (err) {
      janela?.close();
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível gerar os códigos.") });
    } finally {
      setGerando(false);
    }
  }

  function reimprimirGerados() {
    if (!gerados || gerados.gerados.length === 0) return;
    abrirEtiquetas(
      gerados.gerados.map((g) => g.codigo_barras),
      `Etiquetas — ${gerados.gerados.length} equipamento(s) de ensaio`,
      gerados.gerados.map((g) => g.nome),
      (m) => setAviso({ tipo: "erro", texto: m }),
    );
  }

  /** O próximo da lista que ainda não ganhou código (para o "Próximo sem código"). */
  const proximo =
    form?.modo === "dar-codigo"
      ? lista.find((e) => e.id !== form.id && !feitosRef.current.has(e.id))
      : undefined;

  // O pop-up fica montado mesmo se os números falharem depois de salvar: a
  // tela de "pronto" (com a etiqueta para imprimir) não pode sumir por isso.
  const popup = form && (
    <FormEquipamento
      key={`${form.modo}-${form.id ?? "novo"}`}
      modo={form.modo}
      equipamentoId={form.id}
      onFechar={() => setForm(null)}
      onSalvo={(eq) => {
        if (eq && form.modo === "dar-codigo") feitosRef.current.add(eq.id);
        void carregar();
        onMudou();
      }}
      onProximo={proximo ? () => setForm({ modo: "dar-codigo", id: proximo.id }) : undefined}
    />
  );

  // Sem os números ou sem a lista, a tela não finge: um aviso só, com o motivo.
  const falha = erroResumo ?? erroLista;
  if (falha) {
    return (
      <>
        {popup}
        <Indisponivel
          mensagem={falha}
          onTentar={() => {
            onTentarResumo();
            void carregar();
          }}
        />
      </>
    );
  }

  return (
    <>
      {popup}

      <div className="ser-inicio">
        <div className="ser-inicio-txt">
          <h3>Cadastro de equipamento de ensaio</h3>
          <p className="muted">
            Nome, marca, modelo, nº de série e código de barras — pedidos em todo cadastro. O
            certificado de calibração pode ir junto (o sistema lê as datas do PDF) ou ficar para
            depois. É o mesmo cadastro da Gestão da Operação.
          </p>
          <button className="btn-primary btn-grande" onClick={() => setForm({ modo: "novo" })}>
            <IconPlus size={18} /> Cadastrar equipamento
          </button>
        </div>

        <div className="ser-inicio-num">
          <span className="ser-inicio-valor">{resumo ? resumo.estoque.emEstoque : "…"}</span>
          <span className="ser-inicio-rot">em estoque agora</span>
          {resumo && (
            <span className="ser-inicio-pe">
              {resumo.estoque.total} no cadastro ·{" "}
              {resumo.estoque.emServico + resumo.estoque.comCliente + resumo.estoque.emLabExterno}{" "}
              fora
            </span>
          )}
        </div>
      </div>

      {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}

      {gerados && (
        <div className="ser-bipagem eq-gerados">
          <div className="ser-cabecalho">
            <div className="ser-cab-txt">
              <h3>{gerados.gerados.length} código(s) gerado(s)</h3>
              <p className="muted">
                Cada etiqueta sai com o nome do equipamento em cima — cole no equipamento daquele
                nome e bipe uma para conferir.
              </p>
            </div>
            <button type="button" className="icon-btn" title="Fechar" onClick={() => setGerados(null)}>
              <IconClose size={18} />
            </button>
          </div>

          {gerados.gerados.length > 0 && (
            <div className="ser-etiq-acoes">
              <button className="btn-primary btn-grande" onClick={reimprimirGerados}>
                <IconDownload size={18} /> Imprimir as {gerados.gerados.length} etiquetas
              </button>
              <span className="muted">
                Abre numa aba nova, já formatado: Ctrl+P e “Salvar como PDF” ou direto na impressora
                de etiquetas.
              </span>
            </div>
          )}

          {gerados.ignorados.length > 0 && (
            <div className="conf-aviso atencao">
              {gerados.ignorados.length} não receberam código:{" "}
              {gerados.ignorados
                .map((i) => `${nomesDoLote[i.id] ?? `#${i.id}`} (${i.motivo})`)
                .join("; ")}
              .
            </div>
          )}

          {gerados.gerados.length > 0 && (
            <div className="conf-lista">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Equipamento</th>
                    <th>Código</th>
                    <th>Etiqueta</th>
                  </tr>
                </thead>
                <tbody>
                  {gerados.gerados.map((g) => (
                    <tr key={g.id}>
                      <td>{g.nome}</td>
                      <td>
                        <strong>{g.codigo_barras}</strong>
                      </td>
                      <td>
                        <span
                          className="ser-barras"
                          dangerouslySetInnerHTML={{
                            __html: code128SVG(g.codigo_barras, { modulo: 1, altura: 26, legenda: false }),
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="dash-topo">
        <h3 className="dash-secao">
          Já cadastrados sem código de barras{semCodigo ? ` (${lista.length})` : ""}
        </h3>
        <span className="muted dash-dica">
          Os equipamentos de antes desta aba: sem etiqueta, eles não saem nem voltam bipando.
        </span>
      </div>

      {semCodigo === null ? (
        <div className="empty">Carregando...</div>
      ) : lista.length === 0 ? (
        <p className="muted eq-linha-discreta">
          <IconCheck size={14} /> Todos os equipamentos cadastrados já têm código de barras.
        </p>
      ) : (
        <>
          <div className="crud-toolbar">
            <label className="eq-selecionar">
              <input type="checkbox" checked={todosMarcados} onChange={alternarTodos} />
              Selecionar todos{lista.length > MAX_GERAR ? ` (os ${MAX_GERAR} primeiros)` : ""}
            </label>
            <div className="toolbar-right">
              <span className="badge">{marcados.size} marcado(s)</span>
              <button
                className="btn-primary"
                data-grava
                disabled={marcados.size === 0 || gerando}
                onClick={() => void gerarEImprimir()}
              >
                <IconDownload size={16} />{" "}
                {gerando ? "Gerando…" : "Gerar códigos e imprimir etiquetas"}
              </button>
            </div>
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="eq-col-check"></th>
                  <th>Equipamento</th>
                  <th>Nº de série</th>
                  <th>Onde está</th>
                  <th className="col-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((e) => (
                  <tr key={e.id} className="row-clickable" onClick={() => alternar(e.id)}>
                    <td className="eq-col-check">
                      <input
                        type="checkbox"
                        checked={marcados.has(e.id)}
                        onChange={() => alternar(e.id)}
                        onClick={(ev) => ev.stopPropagation()}
                        aria-label={`Marcar ${e.nome}`}
                      />
                    </td>
                    <td>
                      <strong>{e.nome}</strong>
                      {marcaModelo(e) && <div className="an-nota">{marcaModelo(e)}</div>}
                    </td>
                    <td>{e.numero_serie || "—"}</td>
                    <td>
                      <span className={`pill ${tomDaSituacao(e.estoque_situacao)}`}>
                        {e.estoque_situacao}
                      </span>
                    </td>
                    <td className="col-actions">
                      <button
                        className="btn-secondary btn-sm"
                        data-grava
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setForm({ modo: "dar-codigo", id: e.id });
                        }}
                      >
                        Dar código
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/* ======================================================================
 *  O FORMULÁRIO DO EQUIPAMENTO (também usado pelo "Novo" da Gestão)
 * ==================================================================== */

/** O rascunho do certificado, enquanto a pessoa preenche. */
interface CertRascunho {
  /** O PDF escolhido — fica em memória para o "tentar anexar de novo". */
  arquivo: File | null;
  leitura: LeituraCertificadoEquip | null;
  lendo: boolean;
  erroLeitura: string | null;
  calibracao: string;
  validade: string;
  meses: number;
  numero: string;
}

const CERT_VAZIO: CertRascunho = {
  arquivo: null,
  leitura: null,
  lendo: false,
  erroLeitura: null,
  calibracao: "",
  validade: "",
  meses: 12,
  numero: "",
};

/**
 * Tem alguma coisa de certificado para gravar? O nº só conta junto com o PDF:
 * sem arquivo o servidor grava só as datas no equipamento — o nº não teria onde
 * ficar e sumiria calado.
 */
const temCertificado = (c: CertRascunho): boolean => !!(c.arquivo || c.calibracao || c.validade);

/** O que impede de gravar o certificado (null = pode). */
function problemaDoCertificado(c: CertRascunho): string | null {
  if (c.lendo) return "Espere terminar a leitura do PDF.";
  // O servidor exige ao menos uma data: um PDF ilegível anexado sem data
  // nenhuma viraria um certificado que não diz até quando vale.
  if (c.arquivo && !c.calibracao && !c.validade) {
    return "Informe a data de calibração ou tire o PDF.";
  }
  if (c.calibracao && c.validade && c.validade < c.calibracao) {
    return "A validade não pode ser anterior à data de calibração.";
  }
  return null;
}

/**
 * Seleciona o texto inteiro ao receber o foco (código e nº de série): a
 * leitura nova SUBSTITUI a anterior, em vez de grudar nela. O `onMouseUp`
 * segura o clique que, no Chrome e no Safari, desfaria a seleção.
 */
const SELECIONA_TUDO = {
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
    e.currentTarget.dataset.recemFocado = "1";
  },
  onMouseUp: (e: React.MouseEvent<HTMLInputElement>) => {
    if (e.currentTarget.dataset.recemFocado) {
      e.preventDefault();
      delete e.currentTarget.dataset.recemFocado;
    }
  },
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
    delete e.currentTarget.dataset.recemFocado;
  },
};

/**
 * ENTER NÃO ENVIA — passa para o próximo campo. O leitor de código de barras
 * manda um Enter depois de cada leitura; num formulário comum, bipar o nº de
 * série gravaria o cadastro pela metade. Aqui salvar é só pelo botão.
 */
function enterVaiParaOProximo(e: React.KeyboardEvent<HTMLFormElement>) {
  if (e.key !== "Enter") return;
  const alvo = e.target as HTMLElement;
  if (alvo.tagName !== "INPUT" && alvo.tagName !== "SELECT") return;
  e.preventDefault();
  const campos = Array.from(
    e.currentTarget.querySelectorAll<HTMLElement>(
      'input:not([type="file"]):not([type="checkbox"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
    ),
  ).filter((el) => el.offsetParent !== null);
  const i = campos.indexOf(alvo);
  const proximo = campos[i + 1];
  if (proximo) proximo.focus();
  // Último campo (ex.: o código no "Dar código"): o foco fica, então seleciona
  // tudo — a leitura seguinte SUBSTITUI a anterior em vez de grudar nela.
  else if (alvo instanceof HTMLInputElement) alvo.select();
}

interface CamposEquip {
  nome: string;
  fabricante: string;
  modelo: string;
  numero_serie: string;
  observacao: string;
  autorizacao: string;
}

const CAMPOS_VAZIOS: CamposEquip = {
  nome: "",
  fabricante: "",
  modelo: "",
  numero_serie: "",
  observacao: "",
  autorizacao: "",
};

/** O que a tela de "pronto" mostra depois de gravar. */
interface Resultado {
  eq: EquipEnsaio;
  /** novo = cadastro criado agora (tem "Desfazer"); codigo = código gravado; editar = alterações. */
  tipo: "novo" | "codigo" | "editar";
  mostrarEtiqueta: boolean;
  aviso?: string | null;
  /** O equipamento ficou, mas o certificado não foi anexado (o motivo). */
  certFalhou?: string | null;
}

/**
 * Props do pop-up de cadastro/edição. A Gestão da Operação abre este mesmo
 * pop-up no botão "Novo" da aba Equipamentos de Ensaio.
 */
export interface FormEquipamentoProps {
  /**
   * 'novo'       os cinco dados + certificado opcional (cadastro completo);
   * 'editar'     corrige o cadastro (só o nome é obrigatório), a condição de
   *              uso, o código e os certificados;
   * 'dar-codigo' só grava o código de barras num equipamento antigo sem código.
   */
  modo: ModoForm;
  /** Obrigatório em 'editar' e 'dar-codigo'. */
  equipamentoId?: number;
  /** Fecha o pop-up (X, Esc, Cancelar, Fechar). */
  onFechar: () => void;
  /**
   * Chamado a CADA gravação que muda o cadastro (salvou, deu código, anexou
   * certificado, voltou da baixa, desfez o cadastro) — é a deixa para recarregar
   * a lista. Recebe o equipamento como ficou, ou null quando não há um só a
   * mostrar (cadastro desfeito). O pop-up NÃO fecha sozinho depois de um
   * cadastro novo: ele mostra a etiqueta para imprimir.
   */
  onSalvo: (eq: EquipEnsaio | null) => void;
  /** Só em 'dar-codigo': o botão "Próximo sem código". Sem ele, o botão não aparece. */
  onProximo?: () => void;
}

export function FormEquipamento({
  modo,
  equipamentoId,
  onFechar,
  onSalvo,
  onProximo,
}: FormEquipamentoProps) {
  const podeExcluir = usePodeExcluir();

  const [original, setOriginal] = useState<EquipEnsaio | null>(null);
  const [certificados, setCertificados] = useState<CertificadoEquip[]>([]);
  const [carregando, setCarregando] = useState(modo !== "novo");
  const [erroCarga, setErroCarga] = useState<string | null>(null);

  const [campos, setCampos] = useState<CamposEquip>(CAMPOS_VAZIOS);
  const [codigo, setCodigo] = useState("");
  /** "Gerar código": o servidor dá o número na hora de gravar. */
  const [gerar, setGerar] = useState(false);
  const [previa, setPrevia] = useState<string | null>(null);
  const [erroPrevia, setErroPrevia] = useState<string | null>(null);
  const [origemCodigo, setOrigemCodigo] = useState<"leitor" | "camera">("leitor");
  const [camAberta, setCamAberta] = useState(false);
  const [cert, setCert] = useState<CertRascunho>(CERT_VAZIO);

  const [faltando, setFaltando] = useState<string[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  /** Alguém mexeu no formulário (fechar pergunta antes de jogar fora). */
  const [mexeu, setMexeu] = useState(false);
  const [conflitoSerie, setConflitoSerie] = useState<EquipExistente | null>(null);
  const [conflitoCodigo, setConflitoCodigo] = useState<EquipExistente | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [erroResultado, setErroResultado] = useState<string | null>(null);
  const [desfeito, setDesfeito] = useState(false);

  const corpoRef = useRef<HTMLFormElement>(null);
  const nomeRef = useRef<HTMLInputElement>(null);
  const codigoRef = useRef<HTMLInputElement>(null);
  const serieRef = useRef<HTMLInputElement>(null);
  const quadroSerieRef = useRef<HTMLDivElement>(null);

  const carregar = useCallback(async () => {
    if (modo === "novo") {
      setCarregando(false);
      return;
    }
    if (!equipamentoId) {
      setErroCarga("Nenhum equipamento foi escolhido para abrir.");
      setCarregando(false);
      return;
    }
    setCarregando(true);
    try {
      const r = await api.equipamentosEnsaio.equipamento(equipamentoId);
      const eq = r.equipamento;
      setOriginal(eq);
      setCertificados(r.certificados ?? []);
      setCampos({
        nome: eq.nome ?? "",
        fabricante: eq.fabricante ?? "",
        modelo: eq.modelo ?? "",
        numero_serie: eq.numero_serie ?? "",
        observacao: eq.observacao ?? "",
        autorizacao: eq.autorizacao ?? "",
      });
      setCodigo(eq.codigo_barras ?? "");
      setErroCarga(null);
      // "Dar código" num equipamento que ganhou código enquanto a lista estava
      // aberta (outra pessoa, outra tela): nada a gravar — só a etiqueta dele.
      if (modo === "dar-codigo" && String(eq.codigo_barras ?? "").trim()) {
        setResultado({
          eq,
          tipo: "codigo",
          mostrarEtiqueta: true,
          aviso: "Este equipamento já tinha código de barras — nada foi mudado.",
        });
      }
    } catch (err) {
      setErroCarga(mensagemDe(err, "Não foi possível abrir o equipamento."));
    } finally {
      setCarregando(false);
    }
  }, [modo, equipamentoId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const recarregarCertificados = useCallback(async () => {
    if (!equipamentoId) return;
    try {
      const r = await api.equipamentosEnsaio.certificados(equipamentoId);
      setCertificados(r.certificados ?? []);
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível reler os certificados."));
    }
  }, [equipamentoId]);

  // O foco nasce no primeiro campo que interessa: o nome no cadastro, o código
  // quando só se está dando código (o leitor já pode disparar).
  useEffect(() => {
    if (carregando) return;
    const t = setTimeout(() => {
      (modo === "dar-codigo" ? codigoRef.current : nomeRef.current)?.focus();
    }, 40);
    return () => clearTimeout(t);
  }, [carregando, modo]);

  /** Fechar: pergunta antes de jogar fora o que foi digitado. */
  function tentarFechar() {
    if (salvando) return;
    if (mexeu && !resultado && !confirm("Fechar sem salvar? O que foi digitado se perde.")) return;
    onFechar();
  }
  // O Esc é escutado uma vez só; as refs entregam o estado de agora a ele.
  const fecharRef = useRef(tentarFechar);
  fecharRef.current = tentarFechar;
  const camAbertaRef = useRef(camAberta);
  camAbertaRef.current = camAberta;

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Com a câmera aberta, Esc fecha só a câmera.
      if (camAbertaRef.current) setCamAberta(false);
      else fecharRef.current();
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, []);

  function mudarCampo(k: keyof CamposEquip, v: string) {
    setCampos((c) => ({ ...c, [k]: v }));
    setMexeu(true);
    setFaltando((f) => f.filter((x) => x !== k));
    if (k === "numero_serie") setConflitoSerie(null);
  }

  function mudarCodigo(v: string, origem: "leitor" | "camera") {
    setCodigo(v);
    setGerar(false);
    setOrigemCodigo(origem);
    setConflitoCodigo(null);
    setMexeu(true);
    setFaltando((f) => f.filter((x) => x !== "codigo"));
  }

  /** "Gerar código": mostra a PRÉVIA; o número de verdade sai ao gravar. */
  async function pedirGerar() {
    setGerar(true);
    setCodigo("");
    setConflitoCodigo(null);
    setPrevia(null);
    setErroPrevia(null);
    setMexeu(true);
    setFaltando((f) => f.filter((x) => x !== "codigo"));
    try {
      const r = await api.equipamentosEnsaio.proximoCodigo(PREFIXO);
      setPrevia(r.codigo);
    } catch {
      setErroPrevia("Não deu para calcular a prévia agora — o código sai do mesmo jeito ao salvar.");
    }
  }

  /** O PDF foi lido: preenche nome/marca/modelo/série SÓ onde está vazio. */
  function aoLerCertificado(l: LeituraCertificadoEquip) {
    setCampos((c) => ({
      ...c,
      nome: c.nome.trim() ? c.nome : l.equipamento ?? "",
      fabricante: c.fabricante.trim() ? c.fabricante : l.fabricante ?? "",
      modelo: c.modelo.trim() ? c.modelo : l.modelo ?? "",
      numero_serie: c.numero_serie.trim() ? c.numero_serie : l.numero_serie ?? "",
    }));
    setMexeu(true);
  }

  /** Grava o certificado no equipamento. Devolve o motivo da falha (null = gravou). */
  async function anexarSeHouver(id: number): Promise<string | null> {
    if (!temCertificado(cert)) return null;
    try {
      await api.equipamentosEnsaio.salvarCertificado(id, {
        file: cert.arquivo,
        data_calibracao: cert.calibracao,
        data_validade: cert.validade,
        numero: cert.numero.trim(),
        lido_do_pdf: !!cert.leitura?.ok,
      });
      return null;
    } catch (err) {
      return mensagemDe(err, "Não foi possível anexar o certificado.");
    }
  }

  /** O que falta preencher, na ordem da tela. */
  function conferir(): string[] {
    const f: string[] = [];
    if (modo === "dar-codigo") {
      if (!gerar && !codigo.trim()) f.push("codigo");
      return f;
    }
    if (!campos.nome.trim()) f.push("nome");
    if (modo === "novo") {
      if (!campos.fabricante.trim()) f.push("fabricante");
      if (!campos.modelo.trim()) f.push("modelo");
      if (!campos.numero_serie.trim()) f.push("numero_serie");
      if (!gerar && !codigo.trim()) f.push("codigo");
    }
    // Quem já tinha código não pode ficar sem: a etiqueta colada deixaria de
    // ser reconhecida e ninguém saberia por quê.
    if (modo === "editar" && String(original?.codigo_barras ?? "").trim() && !codigo.trim()) {
      f.push("codigo");
    }
    return f;
  }

  async function salvar() {
    if (salvando) return;
    setErro(null);
    setDesfeito(false);
    const f = conferir();
    setFaltando(f);
    if (f.length > 0) {
      setErro(`Falta preencher ${f.length} campo(s) obrigatório(s) — marcados em vermelho.`);
      const alvo = corpoRef.current?.querySelector<HTMLElement>(`[data-campo="${f[0]}"]`);
      alvo?.scrollIntoView({ behavior: "smooth", block: "center" });
      alvo?.querySelector<HTMLElement>("input, select, textarea")?.focus();
      return;
    }
    const problema = modo !== "dar-codigo" ? problemaDoCertificado(cert) : null;
    if (problema) {
      setErro(problema);
      return;
    }

    setSalvando(true);
    try {
      if (modo === "novo") await salvarNovo();
      else if (modo === "dar-codigo") await salvarCodigo();
      else await salvarEdicao();
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível salvar."));
    } finally {
      setSalvando(false);
    }
  }

  async function salvarNovo() {
    const r = await api.equipamentosEnsaio.cadastrar({
      nome: campos.nome.trim(),
      fabricante: campos.fabricante.trim(),
      modelo: campos.modelo.trim(),
      numero_serie: campos.numero_serie.trim(),
      ...(gerar ? { gerar_codigo: true, prefixo: PREFIXO } : { codigo_barras: codigo.trim() }),
      observacao: campos.observacao.trim() || undefined,
      origem: origemCodigo,
    });
    if (!r.ok) {
      if (r.code === "SERIE_DUPLICADA") {
        setConflitoSerie(r.existente);
        setTimeout(() => quadroSerieRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 30);
      } else {
        setConflitoCodigo(r.existente);
        setFaltando(["codigo"]);
      }
      return;
    }
    // O equipamento está gravado daqui em diante; o certificado é um segundo
    // passo — se ele falhar, o cadastro FICA e a tela diz o que faltou.
    const certFalhou = await anexarSeHouver(r.equipamento.id);
    onSalvo(r.equipamento);
    setResultado({
      eq: r.equipamento,
      tipo: "novo",
      mostrarEtiqueta: true,
      aviso: r.aviso ?? null,
      certFalhou,
    });
  }

  async function salvarCodigo() {
    const o = original;
    if (!o) return;
    let eq: EquipEnsaio;
    // Baixado ganha código mas continua sem poder sair: sem este aviso, a
    // etiqueta era colada e só a bipagem de saída contava que ele está baixado.
    let aviso: string | null = null;
    if (gerar) {
      const g = await api.equipamentosEnsaio.gerarCodigos([o.id]);
      const novo = g.gerados.find((x) => x.id === o.id);
      if (!novo) {
        setErro(g.ignorados.find((x) => x.id === o.id)?.motivo ?? "O código não foi gerado — tente de novo.");
        return;
      }
      eq = { ...o, codigo_barras: novo.codigo_barras };
      if (o.estoque_situacao === "Baixado") {
        aviso = `“${o.nome}” está baixado — use “Voltar da baixa” para ele circular de novo.`;
      }
    } else {
      const r = await api.equipamentosEnsaio.atualizar(o.id, { codigo_barras: codigo.trim() });
      if (!r.ok) {
        if (r.code === "CODIGO_DUPLICADO") {
          setConflitoCodigo(r.existente);
          setFaltando(["codigo"]);
        } else {
          setErro(r.error || "Não foi possível gravar o código.");
        }
        return;
      }
      eq = r.equipamento ?? { ...o, codigo_barras: codigo.trim() };
      aviso = r.aviso ?? null;
    }
    onSalvo(eq);
    setResultado({ eq, tipo: "codigo", mostrarEtiqueta: true, aviso });
  }

  async function salvarEdicao() {
    const o = original;
    if (!o) return;
    // SÓ O QUE MUDOU vai para o servidor: um formulário aberto há uma hora não
    // pode devolver a condição de uso antiga por cima da que a bipagem escreveu.
    const dados: Record<string, unknown> = {};
    const chaves: (keyof CamposEquip)[] = [
      "nome",
      "fabricante",
      "modelo",
      "numero_serie",
      "observacao",
      "autorizacao",
    ];
    for (const k of chaves) {
      const novo = campos[k].trim();
      if (novo !== String(o[k] ?? "").trim()) dados[k] = novo;
    }
    const codAntigo = String(o.codigo_barras ?? "").trim();
    const codNovo = codigo.trim();
    let gerarDepois = false;
    if (codAntigo) {
      if (chaveDoCodigo(codNovo) !== chaveDoCodigo(codAntigo)) {
        dados.codigo_barras = codNovo;
        // A trava do servidor: troca só se o código ainda é o que a tela viu.
        dados.codigo_anterior = codAntigo;
      }
    } else if (gerar) {
      gerarDepois = true;
    } else if (codNovo) {
      dados.codigo_barras = codNovo;
    }

    let eq = o;
    let aviso: string | null = null;
    if (Object.keys(dados).length > 0) {
      const r = await api.equipamentosEnsaio.atualizar(o.id, dados);
      if (!r.ok) {
        if (r.code === "SERIE_DUPLICADA") {
          const x = r.existente;
          setErro(
            `Esse nº de série já é do equipamento “${x.nome}”` +
              (x.codigo_barras ? ` (código ${x.codigo_barras})` : "") +
              " — confira o número.",
          );
          setFaltando(["numero_serie"]);
        } else {
          setConflitoCodigo(r.existente);
          setFaltando(["codigo"]);
        }
        return;
      }
      eq = r.equipamento ?? o;
      aviso = r.aviso ?? null;
    }
    if (gerarDepois) {
      const g = await api.equipamentosEnsaio.gerarCodigos([o.id]);
      const novo = g.gerados.find((x) => x.id === o.id);
      if (novo) eq = { ...eq, codigo_barras: novo.codigo_barras };
      else {
        const motivo = g.ignorados.find((x) => x.id === o.id)?.motivo ?? "o código não foi gerado";
        aviso = [aviso, `Código: ${motivo}.`].filter(Boolean).join(" ");
      }
    }
    const certFalhou = await anexarSeHouver(o.id);
    const mudou = Object.keys(dados).length > 0 || gerarDepois || temCertificado(cert);
    if (!mudou) {
      onFechar();
      return;
    }
    onSalvo(eq);
    const codigoMudou = "codigo_barras" in dados || gerarDepois;
    if (!codigoMudou && !aviso && !certFalhou) {
      onFechar();
      return;
    }
    setResultado({
      eq,
      tipo: "editar",
      mostrarEtiqueta: codigoMudou && !!eq.codigo_barras,
      aviso,
      certFalhou,
    });
  }

  /**
   * "USAR ESTE CADASTRO" — o nº de série digitado já é de um equipamento
   * antigo, sem código. Em vez de um segundo cadastro do mesmo equipamento
   * físico, o código (e o certificado) vão para o cadastro que já existe, e
   * marca/modelo/série só completam o que lá estiver VAZIO.
   */
  async function usarExistente() {
    const x = conflitoSerie;
    if (!x || salvando) return;
    setErro(null);
    if (!gerar && !codigo.trim()) {
      setFaltando(["codigo"]);
      setErro("Bipe, digite ou gere o código antes de gravar no cadastro existente.");
      return;
    }
    const problema = problemaDoCertificado(cert);
    if (problema) {
      setErro(problema);
      return;
    }
    setSalvando(true);
    try {
      const completar: Record<string, unknown> = {
        completar_vazios: true,
        fabricante: campos.fabricante.trim(),
        numero_serie: campos.numero_serie.trim(),
      };
      // Modelo de equipamento que está numa OS aberta não muda (o Relatórios
      // casa a OS pelo nome/modelo) — nem para completar o vazio.
      if (!x.em_uso) completar.modelo = campos.modelo.trim();
      const temOQueCompletar =
        (!String(x.fabricante ?? "").trim() && !!campos.fabricante.trim()) ||
        (!x.em_uso && !String(x.modelo ?? "").trim() && !!campos.modelo.trim());

      let eq: EquipEnsaio | null = null;
      let avisoExtra: string | null = null;
      if (gerar) {
        // O código não passa pelo PUT (ele não gera): sai do "gerar códigos",
        // que só dá código a quem ainda não tem — a mesma trava do lote.
        const g = await api.equipamentosEnsaio.gerarCodigos([x.id]);
        const novo = g.gerados.find((i) => i.id === x.id);
        if (!novo) {
          setErro(
            g.ignorados.find((i) => i.id === x.id)?.motivo ??
              "O código não foi gerado — o equipamento pode ter acabado de ganhar um.",
          );
          return;
        }
        eq = { ...x, codigo_barras: novo.codigo_barras } as EquipEnsaio;
        // O código já está gravado: se completar marca/modelo falhar, isso
        // vira um aviso — nunca um "não salvou", que faria gerar outro código.
        if (temOQueCompletar) {
          try {
            const r = await api.equipamentosEnsaio.atualizar(x.id, completar);
            if (r.ok && r.equipamento) {
              eq = { ...r.equipamento, codigo_barras: novo.codigo_barras };
              avisoExtra = r.aviso ?? null;
            } else if (!r.ok) avisoExtra = `Marca/modelo não foram completados: ${r.error}`;
          } catch (err) {
            avisoExtra = `Marca/modelo não foram completados: ${mensagemDe(err, "erro ao gravar")}`;
          }
        }
        try {
          eq = (await api.equipamentosEnsaio.equipamento(x.id)).equipamento;
        } catch {
          /* fica com o que já se sabe: o código novo está no `eq` */
        }
        if (eq?.estoque_situacao === "Baixado") {
          avisoExtra = [avisoExtra, `“${eq.nome}” está baixado — use “Voltar da baixa” para ele circular de novo.`]
            .filter(Boolean)
            .join(" ");
        }
      } else {
        const r = await api.equipamentosEnsaio.atualizar(x.id, { ...completar, codigo_barras: codigo.trim() });
        if (!r.ok) {
          if (r.code === "CODIGO_DUPLICADO") {
            setConflitoCodigo(r.existente);
            setFaltando(["codigo"]);
          } else {
            setErro(r.error || "Não foi possível gravar no cadastro existente.");
          }
          return;
        }
        eq = r.equipamento ?? ({ ...x, codigo_barras: codigo.trim() } as EquipEnsaio);
        avisoExtra = r.aviso ?? null;
      }
      const certFalhou = await anexarSeHouver(x.id);
      onSalvo(eq);
      setConflitoSerie(null);
      setResultado({
        eq,
        tipo: "codigo",
        mostrarEtiqueta: true,
        aviso: [
          "O código foi gravado no cadastro que já existia — nenhum equipamento novo foi criado.",
          avisoExtra,
        ]
          .filter(Boolean)
          .join(" "),
        certFalhou,
      });
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível gravar no cadastro existente."));
    } finally {
      setSalvando(false);
    }
  }

  /** O equipamento do nº de série repetido estava BAIXADO: volta ao estoque. */
  async function voltarDaBaixaExistente() {
    const x = conflitoSerie;
    if (!x) return;
    setSalvando(true);
    setErro(null);
    try {
      const r = await api.equipamentosEnsaio.voltarDaBaixa(x.id);
      setConflitoSerie({ ...x, estoque_situacao: "Em estoque" });
      onSalvo(r.equipamento ?? null);
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível tirar da baixa."));
    } finally {
      setSalvando(false);
    }
  }

  function imprimirEtiqueta(eq: EquipEnsaio) {
    if (!eq.codigo_barras) return;
    abrirEtiquetas([eq.codigo_barras], `Etiqueta ${eq.codigo_barras}`, [eq.nome], setErroResultado);
  }

  /** "Tentar anexar de novo": reusa o PDF que ficou em memória. */
  async function tentarAnexarDeNovo() {
    if (!resultado) return;
    setSalvando(true);
    setErroResultado(null);
    try {
      const falhou = await anexarSeHouver(resultado.eq.id);
      setResultado({ ...resultado, certFalhou: falhou });
      if (!falhou) onSalvo(resultado.eq);
    } finally {
      setSalvando(false);
    }
  }

  /** "Cadastrar outro": formulário limpo, no mesmo pop-up. */
  function cadastrarOutro() {
    setCampos(CAMPOS_VAZIOS);
    setCodigo("");
    setGerar(false);
    setPrevia(null);
    setErroPrevia(null);
    setOrigemCodigo("leitor");
    setCert(CERT_VAZIO);
    setFaltando([]);
    setErro(null);
    setConflitoSerie(null);
    setConflitoCodigo(null);
    setResultado(null);
    setErroResultado(null);
    setDesfeito(false);
    setMexeu(false);
    setTimeout(() => nomeRef.current?.focus(), 40);
  }

  /**
   * "Desfazer este cadastro": para o engano de quem acabou de salvar. O
   * servidor só aceita do próprio autor, nas primeiras 24 h e se o equipamento
   * nunca circulou. O formulário volta preenchido, para corrigir e salvar.
   */
  async function desfazer() {
    if (!resultado) return;
    const eq = resultado.eq;
    if (!confirm(`Desfazer o cadastro de “${eq.nome}”?\n\nEle sai do sistema como se não tivesse sido salvo.`)) return;
    setSalvando(true);
    setErroResultado(null);
    try {
      await api.equipamentosEnsaio.desfazerCadastro(eq.id, eq.codigo_barras ?? "");
      onSalvo(null);
      setResultado(null);
      setDesfeito(true);
      setMexeu(true);
      // O código gerado ficou livre de novo: a prévia é recalculada.
      if (gerar) void pedirGerar();
    } catch (err) {
      setErroResultado(mensagemDe(err, "Não foi possível desfazer o cadastro."));
    } finally {
      setSalvando(false);
    }
  }

  /* --------------------------------------------------------------
   *  A moldura (igual em todas as telas do pop-up)
   * ------------------------------------------------------------ */
  const titulo =
    modo === "novo"
      ? "Cadastrar equipamento de ensaio"
      : modo === "dar-codigo"
        ? `Dar código de barras${original ? ` — ${original.nome}` : ""}`
        : `Editar${original ? ` — ${original.nome}` : " equipamento"}`;
  const subtitulo =
    modo === "novo"
      ? "Os cinco dados de identificação são obrigatórios; o certificado pode ficar para depois."
      : modo === "dar-codigo"
        ? "Bipe a etiqueta que vai ser colada no equipamento — ou gere um código e imprima a etiqueta."
        : "Só o nome é obrigatório aqui. Só o que você mudar é gravado.";

  const moldura = (corpo: React.ReactNode, rodape: React.ReactNode) => (
    <div className="modal-overlay" onMouseDown={tentarFechar}>
      <div
        className="modal-card modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconGauge size={20} />
            </span>
            <div>
              <h3>{titulo}</h3>
              <p className="rf-head-sub">{subtitulo}</p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={tentarFechar} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>
        {corpo}
        <div className="modal-foot">{rodape}</div>
      </div>
    </div>
  );

  /* --------------------------------------------------------------
   *  TELA DE "PRONTO" — a etiqueta, logo depois de gravar
   * ------------------------------------------------------------ */
  if (resultado) {
    const eq = resultado.eq;
    const temEtiqueta = resultado.mostrarEtiqueta && !!eq.codigo_barras;
    const desenho = temEtiqueta ? code128SVG(String(eq.codigo_barras), { modulo: 2, altura: 56 }) : "";
    return moldura(
      <div className="modal-body eq-form">
        <div className="conf-aviso ok">
          {resultado.tipo === "novo"
            ? `“${eq.nome}” foi cadastrado e está no estoque.`
            : resultado.tipo === "codigo"
              ? `Código de barras de “${eq.nome}”: ${eq.codigo_barras ?? "—"}.`
              : `Alterações de “${eq.nome}” salvas.`}
        </div>

        {temEtiqueta && (
          <div className="eq-etiqueta">
            <strong>{eq.nome}</strong>
            {desenho ? (
              <span className="ser-barras" dangerouslySetInnerHTML={{ __html: desenho }} />
            ) : (
              <span className="muted">
                O código “{eq.codigo_barras}” tem caracteres que não viram etiqueta de barras.
              </span>
            )}
            <span className="an-nota">
              {[marcaModelo(eq), eq.numero_serie ? `série ${eq.numero_serie}` : ""]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        )}

        {resultado.aviso && <div className="conf-aviso atencao">{resultado.aviso}</div>}

        {resultado.certFalhou && (
          <div className="conf-aviso erro">
            O equipamento ficou gravado, mas o certificado NÃO foi anexado: {resultado.certFalhou}
            <div className="eq-acoes-aviso">
              <button
                type="button"
                className="btn-secondary btn-sm"
                data-grava
                disabled={salvando}
                onClick={() => void tentarAnexarDeNovo()}
              >
                <IconRefresh size={14} /> Tentar anexar de novo
              </button>
            </div>
          </div>
        )}

        {erroResultado && <div className="conf-aviso erro">{erroResultado}</div>}
      </div>,
      <>
        {temEtiqueta && desenho && (
          <button type="button" className="btn-primary btn-grande" onClick={() => imprimirEtiqueta(eq)}>
            <IconDownload size={18} /> Imprimir etiqueta
          </button>
        )}
        {modo === "novo" && (
          <button type="button" className="btn-secondary" disabled={salvando} onClick={cadastrarOutro}>
            <IconPlus size={16} /> Cadastrar outro
          </button>
        )}
        {resultado.tipo === "novo" && (
          <button
            type="button"
            className="btn-secondary"
            disabled={salvando}
            onClick={() => void desfazer()}
          >
            Desfazer este cadastro
          </button>
        )}
        {modo === "dar-codigo" && onProximo && (
          <button type="button" className="btn-secondary" data-grava onClick={onProximo}>
            Próximo sem código
          </button>
        )}
        <button type="button" className="btn-secondary" disabled={salvando} onClick={onFechar}>
          Fechar
        </button>
      </>,
    );
  }

  /* --------------------------------------------------------------
   *  O FORMULÁRIO
   * ------------------------------------------------------------ */
  if (carregando || erroCarga) {
    return moldura(
      <div className="modal-body">
        {erroCarga ? (
          <Indisponivel mensagem={erroCarga} onTentar={() => void carregar()} />
        ) : (
          <div className="empty">Carregando...</div>
        )}
      </div>,
      <button type="button" className="btn-secondary" onClick={onFechar}>
        Fechar
      </button>,
    );
  }

  const codigoObrigatorio =
    modo !== "editar" || !!String(original?.codigo_barras ?? "").trim();
  /** Gerar só para quem não tem código (trocar um código existente é por leitura). */
  const podeGerar = modo !== "editar" || !String(original?.codigo_barras ?? "").trim();
  const erroCodigo = faltando.includes("codigo");
  const x = conflitoSerie;

  return moldura(
    <form
      ref={corpoRef}
      className="modal-body eq-form"
      noValidate
      onSubmit={(e) => e.preventDefault()}
      onKeyDown={enterVaiParaOProximo}
    >
      {desfeito && (
        <div className="conf-aviso atencao">
          Cadastro desfeito — nada ficou gravado. Corrija o que precisar e salve de novo, ou feche.
        </div>
      )}

      {/* O nº de série já existe: o quadro diz de quem é e o que dá para fazer. */}
      {x && (
        <div className="conf-aviso atencao eq-conflito" ref={quadroSerieRef}>
          <div>
            Esse nº de série já é do equipamento <strong>“{x.nome}”</strong>
            {x.estoque_situacao ? ` (${x.estoque_situacao.toLowerCase()})` : ""}.
            {x.em_uso ? ` ${x.em_uso}.` : ""}
          </div>
          {x.codigo_barras ? (
            <>
              <div>
                Ele já tem o código <strong>{x.codigo_barras}</strong> — use a etiqueta dele. Nada foi
                gravado.
              </div>
              <div className="eq-acoes-aviso">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() =>
                    abrirEtiquetas(
                      [String(x.codigo_barras)],
                      `Etiqueta ${x.codigo_barras}`,
                      [x.nome],
                      setErro,
                    )
                  }
                >
                  <IconDownload size={14} /> Reimprimir etiqueta
                </button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setConflitoSerie(null);
                    setTimeout(() => serieRef.current?.focus(), 0);
                  }}
                >
                  Corrigir o nº de série
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                Ele foi cadastrado sem código de barras. Confira se é o mesmo equipamento — os
                campos vazios no cadastro serão completados com o que você digitou; os preenchidos
                não mudam.
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Digitado agora</th>
                    <th>No cadastro</th>
                  </tr>
                </thead>
                <tbody>
                  {(
                    [
                      ["Nome", campos.nome, x.nome],
                      ["Marca", campos.fabricante, x.fabricante],
                      ["Modelo", campos.modelo, x.modelo],
                      ["Nº de série", campos.numero_serie, x.numero_serie],
                    ] as const
                  ).map(([rotulo, digitado, cadastrado]) => (
                    <tr key={rotulo}>
                      <td>{rotulo}</td>
                      <td>{digitado || "—"}</td>
                      <td>
                        {cadastrado || "—"}
                        {!!String(digitado ?? "").trim() &&
                          !!String(cadastrado ?? "").trim() &&
                          !mesmoTexto(digitado, cadastrado) && (
                            <span className="pill pill-warn eq-pill-mini">diferente</span>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {x.estoque_situacao === "Baixado" && (
                <div>
                  Atenção: este equipamento está <strong>baixado</strong>. Se ele voltou a ser usado,
                  tire da baixa antes.
                </div>
              )}
              <div className="eq-acoes-aviso">
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  data-grava
                  disabled={salvando}
                  onClick={() => void usarExistente()}
                >
                  <IconCheck size={14} /> Usar este cadastro e gravar o código nele
                </button>
                {x.estoque_situacao === "Baixado" && (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    data-grava
                    disabled={salvando}
                    onClick={() => void voltarDaBaixaExistente()}
                  >
                    Voltar da baixa
                  </button>
                )}
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setConflitoSerie(null);
                    setTimeout(() => serieRef.current?.focus(), 0);
                  }}
                >
                  Corrigir o nº de série
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {modo === "dar-codigo" && original && (
        <div className="eq-identidade">
          <strong>{original.nome}</strong>
          <span className="muted">
            {[marcaModelo(original), original.numero_serie ? `série ${original.numero_serie}` : ""]
              .filter(Boolean)
              .join(" · ") || "sem marca, modelo e série no cadastro"}
          </span>
        </div>
      )}

      {modo === "editar" && original && (
        <div className="eq-identidade">
          <span className={`pill ${tomDaSituacao(original.estoque_situacao)}`}>
            {original.estoque_situacao}
          </span>
          <span className="muted">
            {[
              ondeEsta(original),
              original.em_uso ?? "",
              original.liberacao_os ? `Liberado p/ OS ${original.liberacao_os}` : "",
            ]
              .filter(Boolean)
              .join(" · ") || "no estoque"}
          </span>
        </div>
      )}

      <div className="form-grid">
        {modo !== "dar-codigo" && (
          <>
            <Campo
              nome="nome"
              rotulo="Nome do equipamento"
              obrigatorio
              largo
              faltando={faltando}
              ajuda={
                modo === "editar" && original?.em_uso
                  ? `${original.em_uso}: nome e modelo só mudam depois que a OS for concluída.`
                  : undefined
              }
            >
              <input
                ref={nomeRef}
                value={campos.nome}
                onChange={(e) => mudarCampo("nome", e.target.value)}
                placeholder="ex.: Megôhmetro 5 kV"
                autoComplete="off"
              />
            </Campo>
            <Campo nome="fabricante" rotulo="Marca" obrigatorio={modo === "novo"} faltando={faltando}>
              <input
                value={campos.fabricante}
                onChange={(e) => mudarCampo("fabricante", e.target.value)}
                placeholder="ex.: Megabras"
                autoComplete="off"
              />
            </Campo>
            <Campo nome="modelo" rotulo="Modelo" obrigatorio={modo === "novo"} faltando={faltando}>
              <input
                value={campos.modelo}
                onChange={(e) => mudarCampo("modelo", e.target.value)}
                placeholder="ex.: MI 5500e"
                autoComplete="off"
              />
            </Campo>
            <Campo
              nome="numero_serie"
              rotulo="Nº de série"
              obrigatorio={modo === "novo"}
              faltando={faltando}
            >
              <input
                ref={serieRef}
                value={campos.numero_serie}
                onChange={(e) => mudarCampo("numero_serie", e.target.value)}
                placeholder="como está na plaqueta do equipamento"
                autoComplete="off"
                spellCheck={false}
                {...SELECIONA_TUDO}
              />
            </Campo>
          </>
        )}

        {/* O código fica num <div>, não num <label>: com botões dentro, o
            clique no rótulo acionaria o primeiro botão. */}
        <div className={`field field-wide${erroCodigo ? " eq-campo-erro" : ""}`} data-campo="codigo">
          <span>
            Código de barras
            {codigoObrigatorio && <em className="req"> *</em>}
          </span>
          {gerar ? (
            <div className="eq-codigo">
              <div className="ser-faixa">
                {previa ? (
                  <>
                    Vai ser gerado ao salvar: <strong>{previa}</strong>
                  </>
                ) : (
                  erroPrevia ?? "calculando o próximo código…"
                )}
              </div>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => {
                  setGerar(false);
                  setTimeout(() => codigoRef.current?.focus(), 0);
                }}
              >
                Usar a etiqueta que já tem
              </button>
            </div>
          ) : (
            <div className="eq-codigo">
              <input
                ref={codigoRef}
                value={codigo}
                onChange={(e) => mudarCodigo(e.target.value, "leitor")}
                placeholder="bipe a etiqueta com o leitor ou digite"
                autoComplete="off"
                spellCheck={false}
                {...SELECIONA_TUDO}
              />
              {!camAberta && (
                <button type="button" className="btn-secondary btn-sm" onClick={() => setCamAberta(true)}>
                  <IconCamera size={15} /> Ler pela câmera
                </button>
              )}
              {podeGerar && (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  data-grava
                  onClick={() => void pedirGerar()}
                >
                  <IconPlus size={15} /> Gerar código
                </button>
              )}
            </div>
          )}
          {erroCodigo && !conflitoCodigo ? (
            <small className="eq-msg-erro">Bipe, digite ou gere o código</small>
          ) : (
            <small className="muted eq-ajuda">
              {gerar
                ? "A prévia pode mudar se alguém cadastrar ao mesmo tempo — o número definitivo aparece depois de salvar, com a etiqueta para imprimir."
                : modo === "editar" && original?.codigo_barras
                  ? "Trocar o código só quando a etiqueta for substituída: a antiga deixa de ser reconhecida."
                  : "Equipamento sem etiqueta? “Gerar código” e imprima a etiqueta depois de salvar."}
            </small>
          )}
          {conflitoCodigo && (
            <div className="conf-aviso erro">
              Esse código já é do equipamento “{conflitoCodigo.nome}”
              {conflitoCodigo.numero_serie ? ` (série ${conflitoCodigo.numero_serie})` : ""}
              {conflitoCodigo.estoque_situacao ? `, ${conflitoCodigo.estoque_situacao.toLowerCase()}` : ""}.
              Leia outra etiqueta ou gere um código novo.
            </div>
          )}
        </div>
      </div>

      {camAberta && (
        <CameraScanner
          jaConferidos={SEM_LEITURAS}
          // Só setters aqui: a câmera guarda esta função da hora em que abriu,
          // e ler estado por ela pegaria o valor velho.
          onCodigo={(t) => {
            mudarCodigo(t.trim(), "camera");
            setCamAberta(false);
            return Promise.resolve<Leitura>("novo");
          }}
          onFechar={() => setCamAberta(false)}
          onDigitar={() => {
            setCamAberta(false);
            setTimeout(() => codigoRef.current?.focus(), 0);
          }}
        />
      )}

      {modo === "editar" && (
        <div className="form-grid">
          <Campo
            nome="autorizacao"
            rotulo="Condição de uso"
            faltando={faltando}
            ajuda={
              original?.estoque_saida_id
                ? "Está fora: a condição volta sozinha no retorno — a não ser que você a mude aqui."
                : "Em manutenção / Bloqueado aparecem como alerta na saída."
            }
          >
            <select value={campos.autorizacao} onChange={(e) => mudarCampo("autorizacao", e.target.value)}>
              {!campos.autorizacao && <option value="">—</option>}
              {[
                ...CONDICOES_USO,
                ...(original?.autorizacao && !CONDICOES_USO.includes(original.autorizacao)
                  ? [original.autorizacao]
                  : []),
              ].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Campo>
        </div>
      )}

      {modo !== "dar-codigo" && (
        <>
          {modo === "editar" && (
            <div className="eq-bloco">
              <div className="eq-bloco-cab">
                <strong>Certificados anexados</strong>
                <span className="muted">
                  Calibração {dataBR(original?.data_calibracao)} · validade{" "}
                  {dataBR(original?.data_validade)}
                </span>
              </div>
              <ListaCertificados
                certificados={certificados}
                podeRemover={podeExcluir}
                onErro={setErro}
                onRemovido={() => {
                  void recarregarCertificados();
                  if (original) onSalvo(original);
                }}
              />
            </div>
          )}

          <BlocoCertificado
            titulo={modo === "editar" ? "Anexar certificado novo" : "Certificado de calibração"}
            valor={cert}
            setValor={(f) => {
              setCert(f);
              setMexeu(true);
            }}
            onLido={aoLerCertificado}
            serieDigitada={campos.numero_serie}
          />

          <div className="form-grid">
            <Campo nome="observacao" rotulo="Observação" largo faltando={faltando}>
              <textarea
                rows={2}
                value={campos.observacao}
                onChange={(e) => mudarCampo("observacao", e.target.value)}
                placeholder="opcional — acessórios, estojo, onde fica guardado…"
              />
            </Campo>
          </div>
        </>
      )}
    </form>,
    <>
      {erro ? (
        <div className="form-error">{erro}</div>
      ) : (
        <span className="muted eq-rodape-status">
          Enter passa para o próximo campo — salvar é só pelo botão.
        </span>
      )}
      <button type="button" className="btn-secondary" onClick={tentarFechar} disabled={salvando}>
        Cancelar
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={() => void salvar()}
        disabled={salvando || cert.lendo}
      >
        {salvando ? "Salvando…" : modo === "dar-codigo" ? "Gravar o código" : "Salvar"}
      </button>
    </>,
  );
}

/** Um campo do formulário: rótulo, entrada e a linha de ajuda (ou de erro). */
function Campo({
  nome,
  rotulo,
  obrigatorio,
  largo,
  faltando,
  ajuda,
  children,
}: {
  nome: string;
  rotulo: string;
  obrigatorio?: boolean;
  largo?: boolean;
  faltando: string[];
  ajuda?: string;
  children: React.ReactNode;
}) {
  const erro = faltando.includes(nome);
  return (
    <label className={`field${largo ? " field-wide" : ""}${erro ? " eq-campo-erro" : ""}`} data-campo={nome}>
      <span>
        {rotulo}
        {obrigatorio && <em className="req"> *</em>}
      </span>
      {children}
      {erro ? (
        <small className="eq-msg-erro">Preencha este campo</small>
      ) : (
        ajuda && <small className="muted eq-ajuda">{ajuda}</small>
      )}
    </label>
  );
}

/**
 * O CERTIFICADO DE CALIBRAÇÃO — opcional, e de dois jeitos:
 *  1. o PDF do laboratório: o sistema lê e preenche as datas e o nº;
 *  2. só as datas, digitadas (o certificado que veio em papel).
 * O certificado real não traz validade: ela é calculada pelo PERÍODO (12 meses
 * por padrão) e fica editável. Nada é gravado aqui — quem grava é quem usa.
 */
function BlocoCertificado({
  titulo,
  valor,
  setValor,
  onLido,
  serieDigitada,
}: {
  titulo: string;
  valor: CertRascunho;
  setValor: (f: (v: CertRascunho) => CertRascunho) => void;
  onLido?: (l: LeituraCertificadoEquip) => void;
  serieDigitada?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  async function escolher(file: File | null) {
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      setValor((v) => ({ ...v, erroLeitura: "Escolha o certificado em PDF." }));
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setValor((v) => ({ ...v, erroLeitura: "O PDF passa de 25 MB — reduza o arquivo e tente de novo." }));
      return;
    }
    // Arquivo novo = nº novo: o do PDF anterior não pode ir gravado neste (um
    // escaneado sem texto não traria nº nenhum e ficaria com o do outro).
    setValor((v) => ({ ...v, arquivo: file, leitura: null, lendo: true, erroLeitura: null, numero: "" }));
    try {
      const r = await api.equipamentosEnsaio.lerCertificado(file, valor.meses);
      // Se a pessoa trocou de arquivo no meio da leitura, esta chegou tarde.
      setValor((v) =>
        v.arquivo !== file
          ? v
          : {
              ...v,
              lendo: false,
              leitura: r,
              calibracao: r.data_calibracao ?? v.calibracao,
              validade: r.data_validade ?? v.validade,
              numero: r.numero_certificado ?? v.numero,
            },
      );
      onLido?.(r);
    } catch (err) {
      setValor((v) =>
        v.arquivo !== file
          ? v
          : {
              ...v,
              lendo: false,
              erroLeitura: mensagemDe(err, "Não foi possível ler o PDF.") + " Digite as datas abaixo.",
            },
      );
    }
  }

  function tirarPdf() {
    // O nº vai junto: ele só é guardado com o arquivo (ver temCertificado).
    setValor((v) => ({ ...v, arquivo: null, leitura: null, erroLeitura: null, lendo: false, numero: "" }));
    if (inputRef.current) inputRef.current.value = "";
  }

  const leitura = valor.leitura;
  const serieConfere =
    leitura?.numero_serie && serieDigitada?.trim()
      ? mesmoTexto(leitura.numero_serie, serieDigitada)
      : null;

  return (
    <div className="eq-bloco">
      <div className="eq-bloco-cab">
        <strong>{titulo}</strong>
        <span className="muted">opcional — pode anexar depois</span>
      </div>

      <div className="eq-anexo">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          onChange={(e) => void escolher(e.target.files?.[0] ?? null)}
        />
        <button
          type="button"
          className="btn-secondary btn-sm"
          data-grava
          disabled={valor.lendo}
          onClick={() => inputRef.current?.click()}
        >
          <IconFile size={15} /> {valor.arquivo ? "Trocar o PDF" : "Escolher o PDF"}
        </button>
        {valor.arquivo && <span className="eq-arquivo">{valor.arquivo.name}</span>}
        {valor.arquivo && !valor.lendo && (
          <button type="button" className="btn-secondary btn-sm" onClick={tirarPdf}>
            Tirar o PDF
          </button>
        )}
        {valor.lendo && <span className="muted">Lendo o PDF…</span>}
      </div>

      {valor.erroLeitura && <div className="conf-aviso atencao">{valor.erroLeitura}</div>}
      {leitura?.ok && leitura.data_calibracao && (
        <div className="conf-aviso ok">
          Data de calibração achada no PDF: {dataBR(leitura.data_calibracao)} — confira abaixo.
        </div>
      )}
      {leitura?.aviso && <div className="conf-aviso atencao">{leitura.aviso}</div>}
      {serieConfere === false && (
        <div className="conf-aviso atencao">
          O nº de série no PDF é “{leitura?.numero_serie}” — diferente do digitado. Confira se é o
          certificado deste equipamento.
        </div>
      )}

      <div className="form-grid">
        <label className="field">
          <span>Data de calibração</span>
          <input
            type="date"
            value={valor.calibracao}
            onChange={(e) => {
              const iso = e.target.value;
              setValor((v) => ({
                ...v,
                calibracao: iso,
                validade: iso ? somarMeses(iso, v.meses) : v.validade,
              }));
            }}
          />
        </label>
        <label className="field">
          <span>Período de calibração</span>
          <select
            value={valor.meses}
            onChange={(e) => {
              const m = Number(e.target.value);
              setValor((v) => ({
                ...v,
                meses: m,
                validade: v.calibracao ? somarMeses(v.calibracao, m) : v.validade,
              }));
            }}
          >
            {PERIODOS.map((m) => (
              <option key={m} value={m}>
                {m} meses
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Validade</span>
          <input
            type="date"
            value={valor.validade}
            onChange={(e) => {
              const iso = e.target.value;
              setValor((v) => ({ ...v, validade: iso }));
            }}
          />
        </label>
        <label className="field">
          <span>Nº do certificado</span>
          {/* O nº é guardado JUNTO com o PDF (na linha do certificado); sem
              arquivo só as datas vão para o equipamento — digitar o nº ali seria
              perdê-lo sem aviso. */}
          <input
            value={valor.arquivo ? valor.numero : ""}
            disabled={!valor.arquivo}
            onChange={(e) => {
              const n = e.target.value;
              setValor((v) => ({ ...v, numero: n }));
            }}
            placeholder={valor.arquivo ? "opcional" : "só junto com o PDF"}
            autoComplete="off"
          />
        </label>
      </div>

      {leitura && leitura.datas_encontradas.length > 1 && (
        <div className="eq-anexo">
          <span className="muted">Outras datas no documento:</span>
          {leitura.datas_encontradas.map((d) => (
            <button
              key={d}
              type="button"
              className="btn-secondary btn-sm"
              title="Usar esta data como calibração"
              onClick={() =>
                setValor((v) => ({ ...v, calibracao: d, validade: somarMeses(d, v.meses) }))
              }
            >
              {dataBR(d)}
            </button>
          ))}
        </div>
      )}

      <small className="muted eq-ajuda">
        Certificado em papel? Digite só as datas — o PDF (com o nº) pode ser anexado depois, pelo
        Estoque.
      </small>
    </div>
  );
}

/** A lista dos certificados de um equipamento (abrir o PDF; remover só admin). */
function ListaCertificados({
  certificados,
  podeRemover,
  onErro,
  onRemovido,
}: {
  certificados: CertificadoEquip[];
  podeRemover: boolean;
  onErro: (m: string) => void;
  onRemovido?: () => void;
}) {
  async function abrir(c: CertificadoEquip) {
    try {
      await api.equipamentosEnsaio.abrirCertificado(c.id);
    } catch (err) {
      onErro(mensagemDe(err, "Não foi possível abrir o certificado."));
    }
  }

  async function remover(c: CertificadoEquip) {
    if (!confirm(`Remover o certificado “${c.nome_arquivo ?? c.numero ?? c.id}”?\n\nAs datas do equipamento não mudam.`)) {
      return;
    }
    try {
      await api.equipamentosEnsaio.removerCertificado(c.id);
      onRemovido?.();
    } catch (err) {
      onErro(mensagemDe(err, "Não foi possível remover o certificado."));
    }
  }

  if (certificados.length === 0) {
    return <p className="muted eq-linha-discreta">Nenhum certificado anexado ainda.</p>;
  }

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Certificado</th>
          <th>Calibração</th>
          <th>Validade</th>
          <th>Enviado por</th>
          <th className="col-actions"></th>
        </tr>
      </thead>
      <tbody>
        {certificados.map((c) => (
          <tr key={c.id}>
            <td>
              <strong>{c.numero || "sem nº"}</strong>
              {c.nome_arquivo && <div className="an-nota">{c.nome_arquivo}</div>}
            </td>
            <td>{dataBR(c.data_calibracao)}</td>
            <td>{dataBR(c.data_validade)}</td>
            <td>
              {c.enviado_por || "—"}
              {c.criado_em && <div className="an-nota">{diaLocal(c.criado_em)}</div>}
            </td>
            <td className="col-actions">
              {c.nome_arquivo && (
                <button
                  type="button"
                  className="icon-btn icon-btn-brand"
                  title="Abrir o PDF"
                  onClick={() => void abrir(c)}
                >
                  <IconFile size={16} />
                </button>
              )}
              {podeRemover && (
                <button
                  type="button"
                  className="icon-btn icon-btn-danger"
                  title="Remover este certificado"
                  data-grava
                  onClick={() => void remover(c)}
                >
                  <IconTrash size={16} />
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * "VER CERTIFICADOS" — só leitura, aberto também para quem é somente
 * visualização: listar e abrir os PDFs não grava nada.
 */
function CertificadosModal({ equipamento, onFechar }: { equipamento: EquipEnsaio; onFechar: () => void }) {
  const [certificados, setCertificados] = useState<CertificadoEquip[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [erroAbrir, setErroAbrir] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const r = await api.equipamentosEnsaio.certificados(equipamento.id);
      setCertificados(r.certificados ?? []);
      setErro(null);
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível ler os certificados."));
    }
  }, [equipamento.id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") onFechar();
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [onFechar]);

  return (
    <div className="modal-overlay" onMouseDown={onFechar}>
      <div
        className="modal-card modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label={`Certificados de ${equipamento.nome}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconFile size={20} />
            </span>
            <div>
              <h3>Certificados — {equipamento.nome}</h3>
              <p className="rf-head-sub">
                Calibração {dataBR(equipamento.data_calibracao)} · validade{" "}
                {dataBR(equipamento.data_validade)}
                {equipamento.situacao_calibracao ? ` (${equipamento.situacao_calibracao})` : ""}
              </p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onFechar} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body eq-form">
          {erro ? (
            <Indisponivel mensagem={erro} onTentar={() => void carregar()} />
          ) : certificados === null ? (
            <div className="empty">Carregando...</div>
          ) : (
            <ListaCertificados certificados={certificados} podeRemover={false} onErro={setErroAbrir} />
          )}
          {erroAbrir && <div className="conf-aviso erro">{erroAbrir}</div>}
        </div>
        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * "ANEXAR CERTIFICADO NOVO" — o equipamento voltou da calibração externa com
 * certificado novo; é aqui, na bancada do retorno, que ele é anexado.
 */
function AnexarCertificadoModal({
  equipamento,
  onFechar,
  onSalvo,
}: {
  equipamento: { id: number; nome: string };
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const [cert, setCert] = useState<CertRascunho>(CERT_VAZIO);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setErro(null);
    if (!temCertificado(cert)) {
      setErro("Escolha o PDF ou digite a data de calibração.");
      return;
    }
    const problema = problemaDoCertificado(cert);
    if (problema) {
      setErro(problema);
      return;
    }
    setSalvando(true);
    try {
      await api.equipamentosEnsaio.salvarCertificado(equipamento.id, {
        file: cert.arquivo,
        data_calibracao: cert.calibracao,
        data_validade: cert.validade,
        numero: cert.numero.trim(),
        lido_do_pdf: !!cert.leitura?.ok,
      });
      onSalvo();
      onFechar();
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível anexar o certificado."));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={() => !salvando && onFechar()}>
      <div
        className="modal-card modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label={`Certificado novo de ${equipamento.nome}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconFile size={20} />
            </span>
            <div>
              <h3>Certificado novo — {equipamento.nome}</h3>
              <p className="rf-head-sub">Voltou da calibração: anexe o PDF (ou digite as datas).</p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onFechar} title="Fechar" disabled={salvando}>
            <IconClose size={18} />
          </button>
        </div>
        <form
          className="modal-body eq-form"
          noValidate
          onSubmit={(e) => e.preventDefault()}
          onKeyDown={enterVaiParaOProximo}
        >
          <BlocoCertificado titulo="Certificado de calibração" valor={cert} setValor={setCert} />
        </form>
        <div className="modal-foot">
          {erro && <div className="form-error">{erro}</div>}
          <button type="button" className="btn-secondary" onClick={onFechar} disabled={salvando}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void salvar()}
            disabled={salvando || cert.lendo}
          >
            {salvando ? "Anexando…" : "Anexar certificado"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ======================================================================
 *  SUBABA 2 — ESTOQUE
 * ==================================================================== */

function Estoque({
  resumo,
  erroResumo,
  onTentarResumo,
  onMudou,
}: {
  resumo: ResumoEquipEnsaio | null;
  erroResumo: string | null;
  onTentarResumo: () => void;
  onMudou: () => void;
}) {
  const podeExcluir = usePodeExcluir();
  const [busca, setBusca] = useState("");
  const [situacao, setSituacao] = useState("");
  const [calibracao, setCalibracao] = useState("");
  const [soSemCodigo, setSoSemCodigo] = useState(false);
  const [linhas, setLinhas] = useState<EquipEnsaio[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [editando, setEditando] = useState<number | null>(null);
  const [vendoCertificados, setVendoCertificados] = useState<EquipEnsaio | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.equipamentosEnsaio.estoque({
        q: busca.trim(),
        situacao,
        calibracao,
        semCodigo: soSemCodigo,
      });
      // A→Z pelo nome (ver src/ordem.ts); o código desempata os iguais.
      setLinhas(
        [...r.equipamentos].sort(
          (a, b) => compararNomes(a.nome, b.nome) || compararNomes(a.codigo_barras, b.codigo_barras),
        ),
      );
      setErro(null);
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível ler o estoque de equipamentos."));
    } finally {
      setCarregando(false);
    }
  }, [busca, situacao, calibracao, soSemCodigo]);

  useEffect(() => {
    const t = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(t);
  }, [carregar]);

  async function darBaixa(e: EquipEnsaio) {
    const motivo = prompt(
      `Dar baixa em “${e.nome}”?\n\nEscreva o motivo (quebrou, perdeu, descartado…):`,
      "",
    );
    if (motivo === null) return;
    if (!motivo.trim()) {
      alert("Escreva o motivo da baixa — é ele que explica o buraco no inventário depois.");
      return;
    }
    try {
      await api.equipamentosEnsaio.darBaixa(e.id, motivo.trim());
      setAviso({ tipo: "atencao", texto: `“${e.nome}” foi baixado.` });
      await carregar();
      onMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível dar baixa.") });
    }
  }

  async function voltarDaBaixa(e: EquipEnsaio) {
    try {
      await api.equipamentosEnsaio.voltarDaBaixa(e.id);
      setAviso({ tipo: "ok", texto: `“${e.nome}” voltou para o estoque.` });
      await carregar();
      onMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível tirar da baixa.") });
    }
  }

  async function excluir(e: EquipEnsaio) {
    if (!confirm(`Excluir “${e.nome}” do cadastro?\n\nEle some também da Gestão da Operação e do Relatórios.`)) {
      return;
    }
    try {
      await api.equipamentosEnsaio.remover(e.id);
      setAviso({ tipo: "atencao", texto: `“${e.nome}” foi excluído do cadastro.` });
      await carregar();
      onMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível excluir.") });
    }
  }

  // Um aviso só, quando a lista ou os números não vieram (nada de zeros).
  const falha = erro ?? erroResumo;

  return (
    <>
      {editando !== null && (
        <FormEquipamento
          key={`editar-${editando}`}
          modo="editar"
          equipamentoId={editando}
          onFechar={() => setEditando(null)}
          onSalvo={() => {
            void carregar();
            onMudou();
          }}
        />
      )}
      {vendoCertificados && (
        <CertificadosModal equipamento={vendoCertificados} onFechar={() => setVendoCertificados(null)} />
      )}

      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input
            className="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome, série, código, OS ou cliente"
          />
        </div>
        <div className="crud-filtros">
          <label className="crud-filtro">
            <span>Onde está</span>
            <select className="crud-select" value={situacao} onChange={(e) => setSituacao(e.target.value)}>
              <option value="">Todos</option>
              {SITUACOES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="crud-filtro">
            <span>Calibração</span>
            <select className="crud-select" value={calibracao} onChange={(e) => setCalibracao(e.target.value)}>
              <option value="">Todas</option>
              {CALIBRACOES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="crud-filtro">
            <span>Código</span>
            <select
              className="crud-select"
              value={soSemCodigo ? "sem" : ""}
              onChange={(e) => setSoSemCodigo(e.target.value === "sem")}
            >
              <option value="">Todos</option>
              <option value="sem">Só os sem código</option>
            </select>
          </label>
        </div>
        <div className="toolbar-right">
          {!falha && !carregando && <span className="badge">{linhas.length} na lista</span>}
        </div>
      </div>

      {falha ? (
        <Indisponivel
          mensagem={falha}
          onTentar={() => {
            onTentarResumo();
            void carregar();
          }}
        />
      ) : (
        <>
          {/* A QUANTIDADE, grande, antes da tabela: é a primeira pergunta de
              quem abre o estoque. Só aparece com os números de verdade. */}
          {resumo && (
            <div className="ser-quadro">
              <div className="ser-quadro-item destaque">
                <span className="ser-quadro-num">{resumo.estoque.emEstoque}</span>
                <span className="ser-quadro-rot">em estoque</span>
              </div>
              <div className="ser-quadro-item">
                <span className="ser-quadro-num">{resumo.estoque.emServico}</span>
                <span className="ser-quadro-rot">em serviço</span>
              </div>
              <div className="ser-quadro-item">
                <span className="ser-quadro-num">{resumo.estoque.comCliente}</span>
                <span className="ser-quadro-rot">com o cliente</span>
              </div>
              <div className="ser-quadro-item">
                <span className="ser-quadro-num">{resumo.estoque.emLabExterno}</span>
                <span className="ser-quadro-rot">em lab. externo</span>
              </div>
              <div className="ser-quadro-item">
                <span className="ser-quadro-num">{resumo.estoque.faltantes}</span>
                <span className="ser-quadro-rot">faltantes</span>
              </div>
              <div className="ser-quadro-item">
                <span className="ser-quadro-num">{resumo.estoque.baixados}</span>
                <span className="ser-quadro-rot">baixados</span>
              </div>
              <div className="ser-quadro-item total">
                <span className="ser-quadro-num">{resumo.estoque.total}</span>
                <span className="ser-quadro-rot">no cadastro</span>
              </div>
            </div>
          )}

          {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}

          <div className="table-wrap">
            {carregando && linhas.length === 0 ? (
              <div className="empty">Carregando...</div>
            ) : linhas.length === 0 ? (
              <div className="empty">
                Nenhum equipamento com estes filtros. Cadastre na subaba <strong>Cadastro</strong>.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Equipamento</th>
                    <th>Nº de série</th>
                    <th>Calibração</th>
                    <th>Onde está</th>
                    <th>Desde</th>
                    <th className="col-actions">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((e) => {
                    const parado = e.estoque_situacao === "Em estoque" && !e.estoque_saida_id;
                    const onde = ondeEsta(e);
                    return (
                      <tr key={e.id}>
                        <td>
                          {e.codigo_barras ? (
                            <strong>{e.codigo_barras}</strong>
                          ) : (
                            <span className="pill pill-neutral">sem código</span>
                          )}
                        </td>
                        <td>
                          <strong>{e.nome}</strong>
                          {marcaModelo(e) && <div className="an-nota">{marcaModelo(e)}</div>}
                        </td>
                        <td>{e.numero_serie || "—"}</td>
                        <td>
                          {e.data_validade ? formatDate(e.data_validade) : "—"}{" "}
                          {e.situacao_calibracao && (
                            <span className={`pill ${tomDaCalibracao(e.situacao_calibracao)}`}>
                              {e.situacao_calibracao}
                            </span>
                          )}
                          {e.certificado_numero && (
                            <div className="an-nota">Certificado {e.certificado_numero}</div>
                          )}
                        </td>
                        <td>
                          <span className={`pill ${tomDaSituacao(e.estoque_situacao)}`}>
                            {e.estoque_situacao}
                          </span>
                          {onde && <div className="an-nota">{onde}</div>}
                          {e.em_uso && <div className="an-nota">{e.em_uso}</div>}
                          {e.liberacao_os && <div className="an-nota">Liberado p/ OS {e.liberacao_os}</div>}
                          {e.autorizacao && CONDICOES_DE_ATENCAO.includes(e.autorizacao) && (
                            <div className="an-nota">Condição de uso: {e.autorizacao}</div>
                          )}
                          {e.observacao &&
                            (e.estoque_situacao === "Baixado" || e.estoque_situacao === "Faltante") && (
                              <div className="an-nota">{e.observacao}</div>
                            )}
                        </td>
                        <td>{diaLocal(e.estoque_atualizado_em ?? e.created_at)}</td>
                        <td className="col-actions">
                          {/* Só leitura, e por isso SEM data-grava: quem só
                              visualiza também abre os certificados. */}
                          <button
                            className="icon-btn icon-btn-brand"
                            title="Ver certificados"
                            onClick={() => setVendoCertificados(e)}
                          >
                            <IconFile size={16} />
                          </button>
                          <button
                            className="icon-btn"
                            title="Editar o cadastro"
                            data-grava
                            onClick={() => setEditando(e.id)}
                          >
                            <IconEdit size={16} />
                          </button>
                          {/* Fora (em serviço, com cliente, em lab. externo)
                              NÃO tem baixa nem volta: muda só pela bipagem
                              do retorno, senão a saída esperaria para sempre. */}
                          {(parado || e.estoque_situacao === "Faltante") && (
                            <button
                              className="icon-btn"
                              title="Dar baixa (quebrou, perdeu, descartado)"
                              data-grava
                              onClick={() => void darBaixa(e)}
                            >
                              <IconAlert size={16} />
                            </button>
                          )}
                          {e.estoque_situacao === "Baixado" && (
                            <button
                              className="icon-btn icon-btn-brand"
                              title="Voltar da baixa (volta para o estoque)"
                              data-grava
                              onClick={() => void voltarDaBaixa(e)}
                            >
                              <IconCheck size={16} />
                            </button>
                          )}
                          {e.codigo_barras && (
                            <button
                              className="icon-btn"
                              title="Imprimir a etiqueta deste equipamento"
                              onClick={() =>
                                abrirEtiquetas(
                                  [String(e.codigo_barras)],
                                  `Etiqueta ${e.codigo_barras}`,
                                  [e.nome],
                                  (m) => setAviso({ tipo: "erro", texto: m }),
                                )
                              }
                            >
                              <IconDownload size={16} />
                            </button>
                          )}
                          {podeExcluir &&
                            !e.estoque_saida_id &&
                            (e.estoque_situacao === "Em estoque" || e.estoque_situacao === "Baixado") && (
                              <button
                                className="icon-btn icon-btn-danger"
                                title="Excluir do cadastro"
                                data-grava
                                onClick={() => void excluir(e)}
                              >
                                <IconTrash size={16} />
                              </button>
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </>
  );
}

/* ======================================================================
 *  BUSCA DE CLIENTE — nome ou CNPJ, com o que o sistema já conhece
 * ==================================================================== */

function BuscaCliente({
  nome,
  cnpj,
  onEscolher,
}: {
  nome: string;
  cnpj: string;
  onEscolher: (nome: string, cnpj: string) => void;
}) {
  const [achados, setAchados] = useState<ClienteEquip[]>([]);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const termo = nome.trim();
    if (termo.length < 2) {
      setAchados([]);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(() => {
      api.equipamentosEnsaio
        .clientes(termo, controller.signal)
        .then((r) => {
          setAchados(r.clientes.slice(0, 8));
          setAberto(true);
        })
        // É sugestão, não lista: sem ela a pessoa só digita o nome inteiro.
        .catch(() => setAchados([]));
    }, 300);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [nome]);

  return (
    <div className="ser-form-linha">
      <label className="ser-campo ser-campo-busca">
        <span>Cliente</span>
        <input
          value={nome}
          onChange={(e) => onEscolher(e.target.value, cnpj)}
          placeholder="digite o nome ou o CNPJ e escolha na lista"
          autoComplete="off"
        />
        {aberto && achados.length > 0 && (
          <div className="ser-sugestoes">
            {achados.map((c) => (
              <button
                key={c.nome}
                type="button"
                className="ser-sugestao"
                onClick={() => {
                  onEscolher(c.nome, c.cnpj ?? cnpj);
                  setAberto(false);
                }}
              >
                <strong>{c.nome}</strong>
                <span className="muted">
                  {c.cnpj ? c.cnpj : "sem CNPJ no cadastro"} · {c.fontes.join(", ")}
                </span>
              </button>
            ))}
          </div>
        )}
      </label>
      <label className="ser-campo">
        <span>CNPJ</span>
        <input
          value={cnpj}
          onChange={(e) => onEscolher(nome, e.target.value)}
          placeholder="00.000.000/0000-00"
          autoComplete="off"
        />
      </label>
    </div>
  );
}

/* ======================================================================
 *  SUBABA 3 — SAÍDA
 * ==================================================================== */

function Saidas({
  saidaAberta,
  onAbrirSaida,
  onCadastroMudou,
  onContagemMudou,
}: {
  saidaAberta: number | null;
  onAbrirSaida: (id: number | null) => void;
  onCadastroMudou: () => void;
  onContagemMudou: () => void;
}) {
  const podeExcluir = usePodeExcluir();
  const [saidas, setSaidas] = useState<SaidaEquip[]>([]);
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [criando, setCriando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.equipamentosEnsaio.saidas({ q: busca.trim() });
      setSaidas(r.saidas);
      setErro(null);
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível ler as saídas."));
    } finally {
      setCarregando(false);
    }
  }, [busca]);

  useEffect(() => {
    if (saidaAberta) return;
    const t = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(t);
  }, [carregar, saidaAberta]);

  async function excluirSaida(s: SaidaEquip) {
    if (
      !confirm(
        `Apagar a saída #${s.id}?\n\nOs equipamentos dela voltam para o estoque. ` +
          "Para uma saída que já circulou, o certo é encerrar pelo Retorno.",
      )
    )
      return;
    try {
      const r = await api.equipamentosEnsaio.removerSaida(s.id);
      setAviso({ tipo: "atencao", texto: r.mensagem ?? `Saída #${s.id} apagada.` });
      await carregar();
      onCadastroMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível apagar a saída.") });
    }
  }

  if (saidaAberta) {
    return (
      <BipagemSaida
        id={saidaAberta}
        onFechar={() => {
          onAbrirSaida(null);
          void carregar();
        }}
        onCadastroMudou={onCadastroMudou}
      />
    );
  }

  return (
    <>
      {criando && (
        <NovaSaida
          onCancelar={() => setCriando(false)}
          onCriada={(id) => {
            setCriando(false);
            onContagemMudou();
            onAbrirSaida(id);
          }}
        />
      )}

      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input
            className="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="OS, cliente, laboratório ou CNPJ"
          />
        </div>
        <div className="toolbar-right">
          {!erro && !carregando && <span className="badge">{saidas.length} saída(s)</span>}
          <button className="btn-primary" onClick={() => setCriando(true)}>
            <IconPlus size={16} /> Nova saída
          </button>
        </div>
      </div>

      {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}

      {erro ? (
        <Indisponivel mensagem={erro} onTentar={() => void carregar()} />
      ) : (
        <div className="table-wrap">
          {carregando && saidas.length === 0 ? (
            <div className="empty">Carregando...</div>
          ) : saidas.length === 0 ? (
            <div className="empty">
              Nenhuma saída registrada. Clique em <strong>Nova saída</strong> para mandar
              equipamentos para uma OS, para o cliente ou para calibração.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Data</th>
                  <th>Destino</th>
                  <th>OS / Cliente</th>
                  <th>Equipamentos</th>
                  <th>Situação</th>
                  <th className="col-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {saidas.map((x) => {
                  const bipados = Number(x.bipados) || 0;
                  const retornados = Number(x.retornados) || 0;
                  const previsto = Number(x.quantidade_prevista) || 0;
                  return (
                    <tr key={x.id} className="row-clickable" onDoubleClick={() => onAbrirSaida(x.id)}>
                      <td>{x.id}</td>
                      <td>
                        {formatDate(x.data)}
                        {quemAbriu(x) && <div className="an-nota">{quemAbriu(x)}</div>}
                      </td>
                      <td>
                        <SeloDestino destino={x.destino} />
                      </td>
                      <td>
                        {x.destino === LAB_EXTERNO ? (
                          <>
                            <strong>{x.laboratorio_externo ?? "—"}</strong>
                            {x.os_numero && <div className="an-nota">OS {x.os_numero}</div>}
                          </>
                        ) : x.destino === "Cliente" ? (
                          <>
                            <strong>{x.cliente_nome ?? "—"}</strong>
                            {x.cliente_cnpj && <div className="an-nota">{x.cliente_cnpj}</div>}
                          </>
                        ) : (
                          <strong>OS {x.os_numero ?? "—"}</strong>
                        )}
                      </td>
                      <td>
                        {bipados}
                        {previsto > 0 && ` de ${previsto}`}
                        {x.status !== "Em separação" && (
                          <div className="an-nota">{retornados} já voltaram</div>
                        )}
                      </td>
                      <td>
                        <span className={`pill ${tomDoLote(x.status)}`}>{x.status}</span>
                      </td>
                      <td className="col-actions">
                        <button
                          className="icon-btn icon-btn-brand"
                          title="Abrir a saída"
                          onClick={() => onAbrirSaida(x.id)}
                        >
                          <IconSearch size={16} />
                        </button>
                        {podeExcluir && (
                          <button
                            className="icon-btn icon-btn-danger"
                            title="Apagar a saída"
                            data-grava
                            onClick={() => void excluirSaida(x)}
                          >
                            <IconTrash size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
}

/** O selo do destino na lista (ícone + palavra). */
function SeloDestino({ destino }: { destino: string }) {
  if (destino === LAB_EXTERNO) {
    return (
      <span className="an-selos">
        <IconFlask size={14} /> Calibração / lab. externo
      </span>
    );
  }
  if (destino === "Cliente") {
    return (
      <span className="an-selos">
        <IconTruck size={14} /> Cliente
      </span>
    );
  }
  return (
    <span className="an-selos">
      <IconBox size={14} /> Serviço
    </span>
  );
}

/**
 * NOVA SAÍDA — o pop-up que abre o lote. As perguntas mudam com o destino
 * porque as obrigações mudam: serviço precisa de OS, cliente precisa de quem
 * recebe, e calibração externa precisa do laboratório (é de quem se cobra a
 * volta — e o certificado novo).
 */
function NovaSaida({
  onCancelar,
  onCriada,
}: {
  onCancelar: () => void;
  onCriada: (id: number) => void;
}) {
  const [destino, setDestino] = useState<"Serviço" | "Cliente" | typeof LAB_EXTERNO>("Serviço");
  const [os, setOs] = useState("");
  const [labExterno, setLabExterno] = useState("");
  const [labsConhecidos, setLabsConhecidos] = useState<string[]>([]);
  const [clienteNome, setClienteNome] = useState("");
  const [clienteCnpj, setClienteCnpj] = useState("");
  const [data, setData] = useState(hojeISO());
  const [quantidade, setQuantidade] = useState("");
  const [observacao, setObservacao] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Os laboratórios já usados viram sugestão — evita "Lab X" e "LAB X LTDA"
  // virarem dois destinos diferentes.
  useEffect(() => {
    if (destino !== LAB_EXTERNO || labsConhecidos.length) return;
    api.equipamentosEnsaio
      .laboratoriosExternos()
      .then((r) => setLabsConhecidos(r.laboratorios))
      .catch(() => {
        /* é só sugestão: sem ela, digita-se o nome */
      });
  }, [destino, labsConhecidos.length]);

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    // É o teto da bipagem: lançou 3, saem no máximo 3.
    if (!(Number(quantidade) >= 1)) {
      setErro("Informe quantos equipamentos vão sair.");
      return;
    }
    if (destino === "Serviço" && !os.trim()) {
      setErro("Informe a OS do serviço.");
      return;
    }
    if (destino === "Cliente" && !clienteNome.trim()) {
      setErro("Informe o cliente que vai receber os equipamentos.");
      return;
    }
    if (destino === LAB_EXTERNO && !labExterno.trim()) {
      setErro("Informe o laboratório externo que vai receber os equipamentos.");
      return;
    }
    setSalvando(true);
    try {
      const r = await api.equipamentosEnsaio.criarSaida({
        destino,
        os_numero: os.trim(),
        cliente_nome: destino === LAB_EXTERNO ? "" : clienteNome.trim(),
        cliente_cnpj: destino === LAB_EXTERNO ? "" : clienteCnpj.trim(),
        laboratorio_externo: destino === LAB_EXTERNO ? labExterno.trim() : "",
        data,
        quantidade_prevista: Math.trunc(Number(quantidade)) || 0,
        observacao: observacao.trim(),
      });
      onCriada(Number(r.saida.id));
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível abrir a saída."));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancelar}>
      <form className="modal-card modal-wide" onMouseDown={(e) => e.stopPropagation()} onSubmit={salvar}>
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconTruck size={20} />
            </span>
            <div>
              <h3>Nova saída de equipamento</h3>
              <p className="rf-head-sub">Diga para onde vão e quantos são — a bipagem abre em seguida.</p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onCancelar} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body ser-form">
          <div className="ser-escolha">
            <span className="ser-escolha-rot">Para onde vai</span>
            <div className="ser-escolha-botoes">
              <button
                type="button"
                className={`ser-chip ${destino === "Serviço" ? "ativo" : ""}`}
                onClick={() => setDestino("Serviço")}
              >
                <IconBox size={16} /> Serviço Sercamp (OS)
              </button>
              <button
                type="button"
                className={`ser-chip ${destino === "Cliente" ? "ativo" : ""}`}
                onClick={() => setDestino("Cliente")}
              >
                <IconTruck size={16} /> Envio para o cliente
              </button>
              <button
                type="button"
                className={`ser-chip ${destino === LAB_EXTERNO ? "ativo" : ""}`}
                onClick={() => setDestino(LAB_EXTERNO)}
              >
                <IconFlask size={16} /> Calibração / laboratório externo
              </button>
            </div>
          </div>

          {destino === LAB_EXTERNO ? (
            <div className="ser-form-linha">
              <label className="ser-campo">
                <span>Laboratório externo</span>
                <input
                  value={labExterno}
                  onChange={(e) => setLabExterno(e.target.value)}
                  placeholder="quem vai calibrar"
                  list="eq-labs-externos"
                  autoFocus
                />
                <datalist id="eq-labs-externos">
                  {labsConhecidos.map((l) => (
                    <option key={l} value={l} />
                  ))}
                </datalist>
              </label>
              <label className="ser-campo">
                <span>OS (opcional)</span>
                <input value={os} onChange={(e) => setOs(e.target.value)} placeholder="se houver uma OS" />
              </label>
            </div>
          ) : destino === "Serviço" ? (
            <div className="ser-form-linha">
              <label className="ser-campo">
                <span>OS do serviço</span>
                <input
                  value={os}
                  onChange={(e) => setOs(e.target.value)}
                  placeholder="número da ordem de serviço"
                  autoFocus
                />
              </label>
              <label className="ser-campo">
                <span>Cliente (opcional)</span>
                <input
                  value={clienteNome}
                  onChange={(e) => setClienteNome(e.target.value)}
                  placeholder="de quem é o serviço"
                />
              </label>
            </div>
          ) : (
            <>
              <BuscaCliente
                nome={clienteNome}
                cnpj={clienteCnpj}
                onEscolher={(n, c) => {
                  setClienteNome(n);
                  setClienteCnpj(c);
                }}
              />
              <div className="ser-form-linha">
                <label className="ser-campo">
                  <span>OS (opcional)</span>
                  <input value={os} onChange={(e) => setOs(e.target.value)} placeholder="se o envio já tem OS" />
                </label>
              </div>
            </>
          )}

          <div className="ser-form-linha">
            <label className="ser-campo">
              <span>Data</span>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </label>
            <label className="ser-campo">
              <span>Quantidade de equipamentos</span>
              <input
                type="number"
                min={1}
                step={1}
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value)}
                placeholder="quantos vão sair"
              />
            </label>
          </div>

          <label className="ser-campo">
            <span>Observação</span>
            <input value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="opcional" />
          </label>

          <p className="muted">
            A quantidade é o limite da bipagem: depois de salvar, a área de leitura abre e aceita só
            essa quantidade de equipamentos.
          </p>

          {erro && <div className="conf-aviso erro">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="submit" className="btn-primary" disabled={salvando}>
            <IconCheck size={16} /> Abrir a bipagem
          </button>
          <button type="button" className="btn-secondary" onClick={onCancelar}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

/** As pílulas de alerta de um item (persistem: vêm do servidor a cada leitura da saída). */
function Alertas({ alertas }: { alertas: string[] | undefined }) {
  if (!alertas || alertas.length === 0) return null;
  return (
    <div className="eq-alertas">
      {alertas.map((a) => (
        <span key={a} className="pill pill-warn eq-pill-mini">
          {a}
        </span>
      ))}
    </div>
  );
}

/** O código lido é o nº de série de um equipamento sem código (a saída perguntou). */
interface SemCodigoPendente {
  codigo: string;
  origem: "leitor" | "camera";
  equipamento: { id: number; nome: string; modelo: string | null; numero_serie: string | null };
  mensagem?: string;
}

/** O que está sendo corrigido numa saída já confirmada. */
type ModoCorrecao =
  | { tipo: "dados" }
  | { tipo: "incluir" }
  | { tipo: "trocar"; item: ItemSaidaEquip }
  | { tipo: "tirar"; item: ItemSaidaEquip };

const nomeDoItem = (i: ItemSaidaEquip) => `${i.equipamento_nome || i.codigo} (${i.codigo})`;

/**
 * CORRIGIR SAÍDA JÁ CONFIRMADA.
 *
 * Depois de confirmada, a saída trava — e é o certo no dia a dia. Mas a
 * realidade escapa: o equipamento foi trocado na hora de sair, a OS foi
 * digitada errada, a saída foi encerrada antes de alguém registrar a troca.
 * Este painel só aparece para quem tem a permissão (o servidor manda
 * `pode_corrigir`) e só abre num clique explícito, para ninguém mexer num
 * registro fechado sem querer. O motivo é obrigatório: cada correção fica
 * registrada na própria saída, com quem fez.
 *
 * Declarado fora da tela de bipagem: componente aninhado é remontado a cada
 * render do pai, e os campos perderiam o que a pessoa digitou.
 */
function CorrecaoSaida({
  saida,
  modo,
  onModo,
  onFeito,
  onFechar,
}: {
  saida: SaidaEquip;
  modo: ModoCorrecao | null;
  onModo: (m: ModoCorrecao | null) => void;
  onFeito: (mensagem: string) => Promise<void>;
  onFechar: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [codigo, setCodigo] = useState("");
  /** null = a pessoa ainda não disse — a escolha é obrigatória, sem padrão. */
  const [aindaFora, setAindaFora] = useState<boolean | null>(null);
  const [os, setOs] = useState("");
  const [cliente, setCliente] = useState("");
  const [lab, setLab] = useState("");
  const [data, setData] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [observacao, setObservacao] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const ehLab = saida.destino === LAB_EXTERNO;
  const formRef = useRef<HTMLFormElement>(null);

  // Trocar de ação começa do zero; "Editar" começa com os dados de agora.
  useEffect(() => {
    setCodigo("");
    setAindaFora(null);
    setErro(null);
    if (modo?.tipo === "dados") {
      setOs(saida.os_numero ?? "");
      setCliente(saida.cliente_nome ?? "");
      setLab(saida.laboratorio_externo ?? "");
      setData(String(saida.data ?? "").slice(0, 10));
      setQuantidade(String(saida.quantidade_prevista ?? ""));
      setObservacao(saida.observacao ?? "");
    }
    // O "Trocar"/"Tirar" é clicado na lista, abaixo: o formulário sobe à vista.
    if (modo) formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    // Só quando a AÇÃO muda: a saída recarregada não pode apagar o que se digita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo]);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!modo || salvando) return;
    const m = motivo.trim();
    if (m.length < 3) {
      setErro("Escreva o motivo da correção — ele fica registrado na saída.");
      return;
    }
    if ((modo.tipo === "trocar" || modo.tipo === "incluir") && !codigo.trim()) {
      setErro("Bipe ou digite o código do equipamento que foi.");
      return;
    }
    if ((modo.tipo === "trocar" || modo.tipo === "incluir") && aindaFora === null) {
      setErro("Diga onde ele está agora: ainda com a equipe, ou já de volta no estoque.");
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const id = Number(saida.id);
      let r: { mensagem?: string };
      if (modo.tipo === "dados") {
        const dados: Record<string, unknown> = {
          os_numero: os.trim(),
          data,
          quantidade_prevista: Math.trunc(Number(quantidade)) || 0,
          observacao: observacao.trim(),
        };
        if (ehLab) dados.laboratorio_externo = lab.trim();
        else dados.cliente_nome = cliente.trim();
        r = await api.equipamentosEnsaio.corrigirDados(id, dados, m);
      } else if (modo.tipo === "trocar") {
        r = await api.equipamentosEnsaio.corrigirTrocar(id, modo.item.id, codigo.trim(), aindaFora === true, m);
      } else if (modo.tipo === "incluir") {
        r = await api.equipamentosEnsaio.corrigirIncluir(id, codigo.trim(), aindaFora === true, m);
      } else {
        r = await api.equipamentosEnsaio.corrigirTirar(id, modo.item.id, m);
      }
      setMotivo("");
      await onFeito(r?.mensagem ?? "Correção registrada.");
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível registrar a correção."));
    } finally {
      setSalvando(false);
    }
  }

  const rotuloBotao =
    modo?.tipo === "dados"
      ? "Salvar os dados"
      : modo?.tipo === "trocar"
        ? "Registrar a troca"
        : modo?.tipo === "incluir"
          ? "Incluir na saída"
          : "Tirar da saída";

  return (
    <form ref={formRef} className="eq-correcao" onSubmit={enviar} onClick={(e) => e.stopPropagation()}>
      <div className="eq-correcao-cab">
        <div>
          <strong>Corrigir saída já confirmada</strong>
          <p className="muted">
            Para quando a saída não bate com o que aconteceu — equipamento trocado, OS errada, saída
            encerrada antes da troca. Toda correção fica registrada nesta saída, com o seu nome e o motivo.
          </p>
        </div>
        <button type="button" className="icon-btn" title="Fechar a correção" onClick={onFechar}>
          <IconClose size={16} />
        </button>
      </div>

      <label className="ser-campo">
        <span>Motivo da correção</span>
        <input
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          maxLength={500}
          placeholder="ex.: o DTR estava com a calibração vencida e foi trocado por outro na saída"
        />
      </label>

      <div className="eq-correcao-opcoes">
        <button
          type="button"
          className={`ser-chip ${modo?.tipo === "dados" ? "ativo" : ""}`}
          onClick={() => onModo({ tipo: "dados" })}
        >
          <IconEdit size={15} /> Editar OS e dados
        </button>
        <button
          type="button"
          className={`ser-chip ${modo?.tipo === "incluir" ? "ativo" : ""}`}
          onClick={() => onModo({ tipo: "incluir" })}
        >
          <IconPlus size={15} /> Incluir equipamento
        </button>
        <span className="muted">Para trocar ou tirar um equipamento, use os botões na linha dele, na lista abaixo.</span>
      </div>

      {modo?.tipo === "dados" && (
        <>
          <div className="ser-form-linha">
            <label className="ser-campo">
              <span>{saida.destino === "Serviço" ? "OS do serviço" : "OS (opcional)"}</span>
              <input value={os} onChange={(e) => setOs(e.target.value)} />
            </label>
            {ehLab ? (
              <label className="ser-campo">
                <span>Laboratório externo</span>
                <input value={lab} onChange={(e) => setLab(e.target.value)} />
              </label>
            ) : (
              <label className="ser-campo">
                <span>{saida.destino === "Cliente" ? "Cliente" : "Cliente (opcional)"}</span>
                <input value={cliente} onChange={(e) => setCliente(e.target.value)} />
              </label>
            )}
          </div>
          <div className="ser-form-linha">
            <label className="ser-campo">
              <span>Data</span>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </label>
            <label className="ser-campo">
              <span>Quantidade de equipamentos</span>
              <input
                type="number"
                min={1}
                step={1}
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value)}
              />
            </label>
          </div>
          <label className="ser-campo">
            <span>Observação</span>
            <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
          </label>
          <p className="muted">O destino da saída não muda depois de confirmada.</p>
        </>
      )}

      {(modo?.tipo === "trocar" || modo?.tipo === "incluir") && (
        <>
          {modo.tipo === "trocar" && (
            <p className="eq-correcao-alvo">
              Sai da lista: <strong>{nomeDoItem(modo.item)}</strong>
              {modo.item.retornado_em
                ? " — ele já tinha voltado."
                : " — se está marcado como fora ou faltante, volta para o estoque."}
            </p>
          )}
          <label className="ser-campo">
            <span>{modo.tipo === "trocar" ? "Código do equipamento que foi no lugar" : "Código do equipamento que saiu"}</span>
            <input
              autoFocus
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              // O leitor termina com Enter: aqui ele só encerra a leitura, não envia.
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              placeholder="bipe ou digite o código"
            />
          </label>
          <div className="ser-escolha">
            <span className="ser-escolha-rot">Onde ele está agora?</span>
            <div className="ser-escolha-botoes">
              <button
                type="button"
                className={`ser-chip ${aindaFora === true ? "ativo" : ""}`}
                onClick={() => setAindaFora(true)}
              >
                <IconTruck size={15} /> Ainda está fora (volta pelo Retorno)
              </button>
              <button
                type="button"
                className={`ser-chip ${aindaFora === false ? "ativo" : ""}`}
                onClick={() => setAindaFora(false)}
              >
                <IconBox size={15} /> Já voltou para o estoque
              </button>
            </div>
          </div>
        </>
      )}

      {modo?.tipo === "tirar" && (
        <p className="eq-correcao-alvo">
          Tirar da saída: <strong>{nomeDoItem(modo.item)}</strong>.{" "}
          {modo.item.retornado_em
            ? "Ele já tinha voltado — só sai da lista."
            : "Ele está marcado como fora ou faltante: volta para o estoque."}
        </p>
      )}

      {erro && <div className="conf-aviso erro">{erro}</div>}

      {modo && (
        <div className="eq-correcao-rodape">
          <button type="submit" className="btn-primary" disabled={salvando}>
            <IconCheck size={16} /> {salvando ? "Registrando…" : rotuloBotao}
          </button>
          <button type="button" className="btn-secondary" onClick={() => onModo(null)} disabled={salvando}>
            Cancelar
          </button>
        </div>
      )}
    </form>
  );
}

/** A tela de bipar a saída: contador, leitor, lista e o botão de confirmar. */
function BipagemSaida({
  id,
  onFechar,
  onCadastroMudou,
}: {
  id: number;
  onFechar: () => void;
  onCadastroMudou: () => void;
}) {
  const [dados, setDados] = useState<DetalheSaidaEquip | null>(null);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [semCodigo, setSemCodigo] = useState<SemCodigoPendente | null>(null);
  const [camAberta, setCamAberta] = useState(false);
  /** Fica true quando o servidor pediu confirmação para sair com divergência. */
  const [confirmarDif, setConfirmarDif] = useState(false);
  /** Correção de saída já confirmada: o painel aberto e o que está sendo corrigido. */
  const [corrigindo, setCorrigindo] = useState(false);
  const [modoCorrecao, setModoCorrecao] = useState<ModoCorrecao | null>(null);

  const campoRef = useRef<HTMLInputElement>(null);
  const focar = useCallback(() => {
    if (!camAberta) setTimeout(() => campoRef.current?.focus(), 0);
  }, [camAberta]);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.equipamentosEnsaio.saida(id));
      setErroCarga(null);
    } catch (err) {
      setErroCarga(mensagemDe(err, "Não foi possível abrir a saída."));
    } finally {
      focar();
    }
  }, [id, focar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const registrar = useCallback(
    async (bruto: string, origem: "leitor" | "camera"): Promise<Leitura> => {
      const valor = bruto.trim();
      if (!valor) return "erro";
      try {
        const r = await api.equipamentosEnsaio.biparSaida(id, valor, origem);
        if (r.semCodigo && r.equipamento) {
          // Não é erro de leitura: é a etiqueta de fábrica (o nº de série) de
          // um equipamento antigo. Um clique grava como código e bipa de novo.
          setAviso(null);
          setSemCodigo({ codigo: r.codigo ?? valor, origem, equipamento: r.equipamento, mensagem: r.mensagem });
          return "erro";
        }
        setSemCodigo(null);
        if (r.desconhecida) {
          setAviso({
            tipo: "erro",
            texto: `${r.mensagem ?? `"${valor}" não é de nenhum equipamento.`} Cadastre-o na subaba “Cadastro” e bipe de novo.`,
          });
          return "erro";
        }
        if (r.duplicada) {
          setAviso({ tipo: "atencao", texto: r.mensagem ?? `"${valor}" já está nesta saída.` });
          return "duplicado";
        }
        const nome = r.item?.equipamento_nome || valor;
        const alertas = r.alertas ?? [];
        if (alertas.length > 0) {
          setAviso({ tipo: "atencao", texto: `${nome} entrou na saída — confira: ${alertas.join(" · ")}.` });
        } else {
          setAviso({
            tipo: "ok",
            texto: r.completo
              ? `${nome} separado — a conta fechou (${r.bipados} de ${r.previsto}).`
              : `${nome} separado.`,
          });
        }
        // Completou: a câmera para, não há mais vaga (o teto é do servidor).
        if (r.completo) setCamAberta(false);
        await carregar();
        return alertas.length > 0 ? "alerta" : "novo";
      } catch (err) {
        setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível bipar.") });
        return "erro";
      }
    },
    [id, carregar],
  );

  async function bipar(e: React.FormEvent) {
    e.preventDefault();
    const valor = codigo.trim();
    if (!valor || ocupado) return;
    setOcupado(true);
    try {
      await registrar(valor, "leitor");
    } finally {
      // SEMPRE limpa, até depois de erro: o leitor USB digita por cima do que
      // está no campo, e a leitura seguinte grudaria na errada ("ERRADO" +
      // "EQ-0007") e falharia de novo. O código lido continua no aviso.
      setCodigo("");
      setOcupado(false);
      focar();
    }
  }

  /** "Gravar este código no <nome>": dá o código e bipa de novo, sozinho. */
  async function gravarCodigoLido() {
    const p = semCodigo;
    if (!p) return;
    setOcupado(true);
    try {
      await api.equipamentosEnsaio.codigoDoLeitor(p.equipamento.id, p.codigo);
      setSemCodigo(null);
      onCadastroMudou();
      const r = await registrar(p.codigo, p.origem);
      if (r !== "erro") setCodigo("");
    } catch (err) {
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível gravar o código.") });
    } finally {
      setOcupado(false);
      focar();
    }
  }

  /** Tirar da lista — aberto a quem bipa (não só ao admin): é o desfazer da bancada. */
  async function remover(item: ItemSaidaEquip) {
    const nome = item.equipamento_nome || item.codigo;
    if (!confirm(`Tirar “${nome}” desta saída? Ele volta para o estoque.`)) return;
    try {
      await api.equipamentosEnsaio.removerItemSaida(id, item.id);
      setAviso({ tipo: "atencao", texto: `“${nome}” voltou para o estoque.` });
      await carregar();
    } catch (err) {
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível tirar da saída.") });
    }
  }

  const itens = dados?.itens ?? [];

  async function confirmar(forcar: boolean) {
    // Os alertas não bloqueiam, mas a última palavra é de quem despacha: com
    // equipamento vencido ou em manutenção na caixa, pergunta uma vez.
    const comAlerta = itens.filter((i) => (i.alertas ?? []).length > 0).length;
    if (!forcar && comAlerta > 0 && !confirm(`${comAlerta} equipamento(s) com alerta — confirmar a saída assim?`)) {
      return;
    }
    setOcupado(true);
    try {
      const r = await api.equipamentosEnsaio.confirmarSaida(id, forcar);
      if (r?.divergencia) {
        setConfirmarDif(true);
        setAviso({ tipo: "atencao", texto: r.mensagem ?? "A quantidade bipada não bate com a prevista." });
        return;
      }
      setConfirmarDif(false);
      setAviso({ tipo: "ok", texto: r?.mensagem ?? "Saída registrada." });
      await carregar();
      onCadastroMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível confirmar.") });
    } finally {
      setOcupado(false);
    }
  }

  const jaLidos = useMemo(() => new Set(itens.map((i) => chaveDoCodigo(String(i.codigo)))), [itens]);

  if (erroCarga && !dados) {
    return <Indisponivel mensagem={erroCarga} onTentar={() => void carregar()} />;
  }
  if (!dados) return <div className="empty">Carregando...</div>;

  const saida = dados.saida;
  const emSeparacao = saida.status === "Em separação";
  /** Bipou tudo o que foi lançado: o leitor some até alguém tirar um da lista. */
  const lotada = dados.previsto > 0 && dados.bipados >= dados.previsto;
  const comAlerta = itens.filter((i) => (i.alertas ?? []).length > 0).length;

  return (
    <div className="ser-bipagem" onClick={focar}>
      <div className="ser-cabecalho">
        <div className="ser-cab-txt">
          <h3>{tituloDaSaida(saida, false)}</h3>
          <p className="muted">
            {formatDate(saida.data)}
            {saida.cliente_cnpj ? ` · CNPJ ${saida.cliente_cnpj}` : ""}
            {saida.destino !== "Serviço" && saida.os_numero ? ` · OS ${saida.os_numero}` : ""}
            {saida.observacao ? ` · ${saida.observacao}` : ""}
            {quemAbriu(saida) ? ` · aberta por ${quemAbriu(saida)}` : ""}
          </p>
        </div>
        <span className={`pill ${tomDoLote(saida.status)}`}>{saida.status}</span>
      </div>

      <Contador feito={dados.bipados} previsto={dados.previsto} rotuloFeito="bipados" rotuloPrevisto="previstos" />

      {erroCarga && <Indisponivel mensagem={erroCarga} onTentar={() => void carregar()} />}

      {emSeparacao && lotada ? (
        <div className="conf-aviso ok">
          Os {dados.previsto} equipamento(s) lançados já foram bipados. Confirme a saída — ou tire um
          da lista para bipar outro no lugar.
        </div>
      ) : emSeparacao ? (
        <BarraLeitor
          campoRef={campoRef}
          codigo={codigo}
          setCodigo={setCodigo}
          onBipar={bipar}
          ocupado={ocupado}
          camAberta={camAberta}
          onAbrirCamera={() => setCamAberta(true)}
          rotulo="Bipe os equipamentos que vão sair (ou digite e pressione Enter)"
        />
      ) : (
        <div className="conf-aviso atencao">
          Esta saída já foi confirmada — {dados.retornados} de {dados.bipados} já voltaram. O que
          falta se resolve na subaba <strong>Retorno</strong>.
          {dados.pode_corrigir && !corrigindo && (
            <div className="eq-acoes-aviso">
              <button
                type="button"
                className="btn-secondary btn-sm"
                data-grava
                onClick={(e) => {
                  e.stopPropagation();
                  setCorrigindo(true);
                  setModoCorrecao(null);
                }}
              >
                <IconEdit size={14} /> Corrigir esta saída
              </button>
            </div>
          )}
        </div>
      )}

      {corrigindo && !emSeparacao && (
        <CorrecaoSaida
          saida={saida}
          modo={modoCorrecao}
          onModo={setModoCorrecao}
          onFeito={async (mensagem) => {
            setAviso({ tipo: "ok", texto: mensagem });
            setModoCorrecao(null);
            await carregar();
            onCadastroMudou();
          }}
          onFechar={() => {
            setCorrigindo(false);
            setModoCorrecao(null);
          }}
        />
      )}

      {camAberta && emSeparacao && !lotada && (
        <CameraScanner
          jaConferidos={jaLidos}
          onCodigo={(texto) => registrar(texto, "camera")}
          onFechar={() => {
            setCamAberta(false);
            void carregar();
          }}
          onDigitar={() => {
            setCamAberta(false);
            void carregar();
          }}
        />
      )}

      {semCodigo && (
        <div className="conf-aviso atencao">
          {semCodigo.mensagem ??
            `“${semCodigo.codigo}” é o nº de série de ${semCodigo.equipamento.nome}, que ainda não tem código de barras.`}
          <div className="eq-acoes-aviso">
            <button
              type="button"
              className="btn-secondary btn-sm"
              data-grava
              disabled={ocupado}
              onClick={(e) => {
                e.stopPropagation();
                void gravarCodigoLido();
              }}
            >
              <IconCheck size={14} /> Gravar este código no {semCodigo.equipamento.nome}
            </button>
          </div>
        </div>
      )}

      {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}

      <div className="conf-lista">
        {itens.length === 0 ? (
          <div className="empty">Nenhum equipamento nesta saída ainda — bipe o primeiro.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Código</th>
                <th>Equipamento</th>
                <th>Nº de série</th>
                <th>Bipado por</th>
                <th>Situação</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i, n) => (
                <tr key={i.id}>
                  <td>{itens.length - n}</td>
                  <td>
                    <strong>{i.codigo}</strong>
                  </td>
                  <td>
                    {i.equipamento_nome || "—"}
                    {i.modelo && <span className="muted"> · {i.modelo}</span>}
                  </td>
                  <td>{i.numero_serie || "—"}</td>
                  <td>
                    {i.bipado_por || "—"}
                    {i.origem === "camera" && <span className="conf-tag-camera">câmera</span>}
                    {i.origem === "correcao" && (
                      <span className="conf-tag-camera" title="Entrou por uma correção depois de confirmada">
                        correção
                      </span>
                    )}
                  </td>
                  <td>
                    {i.retornado_em ? (
                      <span className="pill pill-ok">Voltou</span>
                    ) : (
                      <span className={`pill ${tomDaSituacao(i.estoque_situacao)}`}>
                        {i.estoque_situacao || "—"}
                      </span>
                    )}
                    <Alertas alertas={i.alertas} />
                  </td>
                  <td className="col-actions">
                    {emSeparacao && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Tirar este equipamento da saída"
                        data-grava
                        onClick={(e) => {
                          e.stopPropagation();
                          void remover(i);
                        }}
                      >
                        <IconTrash size={16} />
                      </button>
                    )}
                    {corrigindo && !emSeparacao && (
                      <span className="eq-correcao-linha">
                        <button
                          type="button"
                          className={`btn-secondary btn-sm${
                            modoCorrecao?.tipo === "trocar" && modoCorrecao.item.id === i.id ? " ativo" : ""
                          }`}
                          data-grava
                          title="Este não foi (ou voltou trocado): registrar o equipamento que foi no lugar"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModoCorrecao({ tipo: "trocar", item: i });
                          }}
                        >
                          <IconRefresh size={14} /> Trocar
                        </button>
                        <button
                          type="button"
                          className={`btn-secondary btn-sm eq-btn-tirar${
                            modoCorrecao?.tipo === "tirar" && modoCorrecao.item.id === i.id ? " ativo" : ""
                          }`}
                          data-grava
                          title="Tirar este equipamento da saída"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModoCorrecao({ tipo: "tirar", item: i });
                          }}
                        >
                          <IconTrash size={14} /> Tirar
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(dados.correcoes?.length ?? 0) > 0 && (
        <div className="eq-correcoes">
          <h4>
            <IconHistory size={15} /> Correções feitas depois da confirmação ({dados.correcoes!.length})
          </h4>
          <ul>
            {dados.correcoes!.map((c) => (
              <li key={c.id}>
                <div>
                  <strong>{dataHora(c.criado_em)}</strong> · {c.usuario || "—"}
                  {c.origem === "laboratorio" ? " (Laboratório)" : c.origem === "operacional" ? " (Gestão da Operação)" : ""}
                </div>
                <div>{c.detalhe}</div>
                <div className="muted">Motivo: {c.motivo}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="ser-rodape">
        {emSeparacao && (
          <button
            className="btn-primary"
            disabled={ocupado || dados.bipados === 0}
            onClick={() => void confirmar(confirmarDif)}
          >
            <IconSend size={16} /> {confirmarDif ? "Confirmar mesmo com a diferença" : "Confirmar a saída"}
          </button>
        )}
        {emSeparacao && comAlerta > 0 && (
          <span className="muted">{comAlerta} equipamento(s) com alerta nesta saída.</span>
        )}
        <button className="btn-secondary" onClick={onFechar}>
          Voltar para a lista
        </button>
      </div>
    </div>
  );
}

/* ======================================================================
 *  SUBABA 4 — RETORNO
 * ==================================================================== */

/**
 * O RETORNO começa pela LISTA DO QUE ESTÁ FORA — e não por um leitor solto:
 * a pergunta de quem recebe a caixa de volta é "de quem é isto?". Um leitor
 * sem ordem aberta devolveria o equipamento sem ninguém saber de onde ele veio.
 */
function Retornos({
  saidaAberta,
  onAbrirSaida,
  onCadastroMudou,
  onContagemMudou,
}: {
  saidaAberta: number | null;
  onAbrirSaida: (id: number | null) => void;
  onCadastroMudou: () => void;
  onContagemMudou: () => void;
}) {
  const [saidas, setSaidas] = useState<SaidaEquip[]>([]);
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("Enviada");
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.equipamentosEnsaio.saidas({ q: busca.trim(), status });
      setSaidas(r.saidas);
      setErro(null);
    } catch (err) {
      setErro(mensagemDe(err, "Não foi possível ler as saídas."));
    } finally {
      setCarregando(false);
    }
  }, [busca, status]);

  useEffect(() => {
    if (saidaAberta) return;
    const t = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(t);
  }, [carregar, saidaAberta]);

  if (saidaAberta) {
    return (
      <BipagemRetorno
        id={saidaAberta}
        onFechar={() => {
          onAbrirSaida(null);
          void carregar();
        }}
        onCadastroMudou={onCadastroMudou}
        onContagemMudou={onContagemMudou}
      />
    );
  }

  return (
    <>
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input
            className="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="OS, cliente ou laboratório"
          />
        </div>
        <div className="crud-filtros">
          <label className="crud-filtro">
            <span>Mostrar</span>
            <select className="crud-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="Enviada">Aguardando retorno</option>
              <option value="Concluída">Já encerradas</option>
              <option value="">Todas</option>
            </select>
          </label>
        </div>
        <div className="toolbar-right">
          {!erro && !carregando && <span className="badge">{saidas.length} saída(s)</span>}
        </div>
      </div>

      {erro ? (
        <Indisponivel mensagem={erro} onTentar={() => void carregar()} />
      ) : (
        <div className="table-wrap">
          {carregando && saidas.length === 0 ? (
            <div className="empty">Carregando...</div>
          ) : saidas.length === 0 ? (
            <div className="empty">
              {status === "Enviada"
                ? "Nenhum equipamento está fora no momento — nada para receber de volta."
                : "Nenhuma saída nesta situação."}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Data</th>
                  <th>OS / Cliente</th>
                  <th>Voltaram</th>
                  <th>Faltam / faltantes</th>
                  <th>Situação</th>
                  <th className="col-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {saidas.map((x) => {
                  const bipados = Number(x.bipados) || 0;
                  const retornados = Number(x.retornados) || 0;
                  const faltam = bipados - retornados;
                  return (
                    <tr key={x.id} className="row-clickable" onDoubleClick={() => onAbrirSaida(x.id)}>
                      <td>{x.id}</td>
                      <td>{formatDate(x.data)}</td>
                      <td>
                        {x.destino === LAB_EXTERNO ? (
                          <>
                            <strong>{x.laboratorio_externo ?? "—"}</strong>
                            <div className="an-nota">
                              Calibração / lab. externo{x.os_numero ? ` · OS ${x.os_numero}` : ""}
                            </div>
                          </>
                        ) : x.destino === "Cliente" ? (
                          <>
                            <strong>{x.cliente_nome ?? "—"}</strong>
                            {x.cliente_cnpj && <div className="an-nota">{x.cliente_cnpj}</div>}
                          </>
                        ) : (
                          <strong>OS {x.os_numero ?? "—"}</strong>
                        )}
                      </td>
                      <td>
                        {retornados} de {bipados}
                      </td>
                      <td>
                        {faltam <= 0 ? (
                          "—"
                        ) : x.status === "Concluída" ? (
                          // Encerrada com menos: o que não voltou ficou FALTANTE.
                          <span className="pill pill-warn">{faltam} faltante(s)</span>
                        ) : (
                          <strong>{faltam}</strong>
                        )}
                      </td>
                      <td>
                        <span className={`pill ${x.status === "Concluída" ? "pill-ok" : "pill-warn"}`}>
                          {x.status}
                        </span>
                      </td>
                      <td className="col-actions">
                        <button
                          className="icon-btn icon-btn-brand"
                          title="Abrir e bipar o retorno"
                          onClick={() => onAbrirSaida(x.id)}
                        >
                          <IconSearch size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
}

/** A bipagem do retorno: o que falta voltar fica no topo da lista. */
function BipagemRetorno({
  id,
  onFechar,
  onCadastroMudou,
  onContagemMudou,
}: {
  id: number;
  onFechar: () => void;
  onCadastroMudou: () => void;
  onContagemMudou: () => void;
}) {
  const [dados, setDados] = useState<DetalheSaidaEquip | null>(null);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [camAberta, setCamAberta] = useState(false);
  /** O equipamento que voltou da calibração e está recebendo o certificado novo. */
  const [anexando, setAnexando] = useState<{ id: number; nome: string } | null>(null);
  /** Quem já ganhou certificado novo nesta tela (só para mostrar o ✓). */
  const [comCertNovo, setComCertNovo] = useState<Set<number>>(new Set());

  const campoRef = useRef<HTMLInputElement>(null);
  const focar = useCallback(() => {
    if (!camAberta && !anexando) setTimeout(() => campoRef.current?.focus(), 0);
  }, [camAberta, anexando]);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.equipamentosEnsaio.saida(id));
      setErroCarga(null);
    } catch (err) {
      setErroCarga(mensagemDe(err, "Não foi possível abrir a saída."));
    } finally {
      focar();
    }
  }, [id, focar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const registrar = useCallback(
    async (bruto: string, origem: "leitor" | "camera"): Promise<Leitura> => {
      const valor = bruto.trim();
      if (!valor) return "erro";
      try {
        const r = await api.equipamentosEnsaio.retornar(id, valor, origem);
        if (r.duplicada) {
          setAviso({ tipo: "atencao", texto: r.mensagem ?? `"${valor}" já foi devolvido.` });
          return "duplicado";
        }
        const texto = r.mensagem ?? `${valor} devolvido ao estoque.`;
        // O `aviso` é a condição de uso que continua (ex.: estava em
        // manutenção antes de sair): voltou, mas não está liberado.
        if (r.aviso) setAviso({ tipo: "atencao", texto: `${texto} ${r.aviso}` });
        else setAviso({ tipo: "ok", texto });
        // Cada devolução NÃO avisa o sistema (na Gestão da Operação isso
        // recarregaria quatro listas a cada bipagem); os números do painel
        // são relidos quando a pessoa troca de subaba.
        await carregar();
        return r.aviso ? "alerta" : "novo";
      } catch (err) {
        setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível registrar o retorno.") });
        return "erro";
      }
    },
    [id, carregar],
  );

  async function bipar(e: React.FormEvent) {
    e.preventDefault();
    const valor = codigo.trim();
    if (!valor || ocupado) return;
    setOcupado(true);
    try {
      await registrar(valor, "leitor");
    } finally {
      // SEMPRE limpa, até depois de erro: o leitor USB digita por cima do que
      // está no campo, e a leitura seguinte grudaria na errada ("ERRADO" +
      // "EQ-0007") e falharia de novo. O código lido continua no aviso.
      setCodigo("");
      setOcupado(false);
      focar();
    }
  }

  /**
   * Voltou MENOS do que saiu: encerrar marca os que não voltaram como
   * FALTANTE (continuam presos a esta saída, para cobrar, e ficam bloqueados
   * para OS nova). Por isso o servidor exige a segunda confirmação.
   */
  async function encerrar() {
    setOcupado(true);
    try {
      let r = await api.equipamentosEnsaio.concluirSaida(id, false);
      if (r?.pendencia) {
        if (!confirm(r.mensagem ?? "Há equipamentos que não voltaram. Encerrar mesmo assim?")) return;
        const motivo = prompt("Observação (fica escrita no cadastro de cada equipamento faltante):", "");
        if (motivo === null) return;
        r = await api.equipamentosEnsaio.concluirSaida(id, true, motivo);
      }
      setAviso({ tipo: "ok", texto: r?.mensagem ?? "Saída encerrada." });
      await carregar();
      onCadastroMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível encerrar.") });
    } finally {
      setOcupado(false);
    }
  }

  /** A devolução bipada por engano: o equipamento volta a constar como fora. */
  async function desfazerDevolucao(item: ItemSaidaEquip) {
    const nome = item.equipamento_nome || item.codigo;
    if (!confirm(`Desfazer a devolução de “${nome}”?\n\nEle volta a constar como fora.`)) return;
    try {
      const r = await api.equipamentosEnsaio.desfazerRetorno(id, item.id);
      setAviso({ tipo: "atencao", texto: r.mensagem ?? `“${nome}” voltou a constar como fora.` });
      await carregar();
      onContagemMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: mensagemDe(err, "Não foi possível desfazer.") });
    }
  }

  const itens = dados?.itens ?? [];
  /** O que ainda não voltou fica em cima: é o que a pessoa está procurando. */
  const ordenados = useMemo(
    () => [...itens].sort((a, b) => (a.retornado_em ? 1 : 0) - (b.retornado_em ? 1 : 0)),
    [itens],
  );
  const jaVoltaram = useMemo(
    () => new Set(itens.filter((i) => i.retornado_em).map((i) => chaveDoCodigo(String(i.codigo)))),
    [itens],
  );

  if (erroCarga && !dados) {
    return <Indisponivel mensagem={erroCarga} onTentar={() => void carregar()} />;
  }
  if (!dados) return <div className="empty">Carregando...</div>;

  const saida = dados.saida;
  const encerrada = saida.status === "Concluída";
  const daCalibracao = saida.destino === LAB_EXTERNO;
  /** Encerrada com faltantes: o leitor continua, para o faltante que aparecer depois. */
  const aceitaBipar = !encerrada || dados.pendentes > 0;
  /**
   * Voltou DEPOIS do encerramento = era faltante (no fechamento automático o
   * retorno e o encerramento têm a mesma hora, então não conta). Mesma regra
   * do servidor, para a lixeira só aparecer onde o desfazer é aceito.
   */
  const exFaltante = (i: { retornado_em?: string | null }) =>
    !!saida.concluida_em && !!i.retornado_em && String(i.retornado_em) > String(saida.concluida_em);
  const teveFaltante = encerrada && (dados.pendentes > 0 || itens.some(exFaltante));

  return (
    <div className="ser-bipagem" onClick={focar}>
      {anexando && (
        <AnexarCertificadoModal
          equipamento={anexando}
          onFechar={() => {
            setAnexando(null);
            setTimeout(() => campoRef.current?.focus(), 0);
          }}
          onSalvo={() => {
            const eqId = anexando.id;
            setComCertNovo((s) => new Set(s).add(eqId));
            setAviso({ tipo: "ok", texto: `Certificado novo anexado em “${anexando.nome}”.` });
            onCadastroMudou();
          }}
        />
      )}

      <div className="ser-cabecalho">
        <div className="ser-cab-txt">
          <h3>{tituloDaSaida(saida, true)}</h3>
          <p className="muted">
            Saída de {formatDate(saida.data)}
            {saida.cliente_cnpj ? ` · CNPJ ${saida.cliente_cnpj}` : ""} — bipe cada equipamento que
            voltou; ele é devolvido ao estoque na hora.
            {daCalibracao ? " Chegou com certificado novo? Anexe na linha dele." : ""}
          </p>
        </div>
        <span className={`pill ${encerrada ? "pill-ok" : "pill-warn"}`}>{saida.status}</span>
      </div>

      <Contador feito={dados.retornados} previsto={dados.bipados} rotuloFeito="voltaram" rotuloPrevisto="saíram" />

      {erroCarga && <Indisponivel mensagem={erroCarga} onTentar={() => void carregar()} />}

      {encerrada && (
        <div className={`conf-aviso ${dados.pendentes > 0 ? "atencao" : "ok"}`}>
          {dados.pendentes > 0
            ? `Devolução encerrada com ${dados.pendentes} equipamento(s) faltante(s). Se algum aparecer, bipe aqui que ele volta ao estoque.`
            : "Esta saída já foi encerrada — todos voltaram."}
        </div>
      )}
      {aceitaBipar && (
        <BarraLeitor
          campoRef={campoRef}
          codigo={codigo}
          setCodigo={setCodigo}
          onBipar={bipar}
          ocupado={ocupado}
          camAberta={camAberta}
          onAbrirCamera={() => setCamAberta(true)}
          rotulo="Bipe os equipamentos que voltaram (ou digite e pressione Enter)"
        />
      )}

      {camAberta && aceitaBipar && (
        <CameraScanner
          jaConferidos={jaVoltaram}
          onCodigo={(texto) => registrar(texto, "camera")}
          onFechar={() => {
            setCamAberta(false);
            void carregar();
          }}
          onDigitar={() => {
            setCamAberta(false);
            void carregar();
          }}
        />
      )}

      {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}

      <div className="conf-lista">
        {ordenados.length === 0 ? (
          <div className="empty">Esta saída não tem equipamentos.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Equipamento</th>
                <th>Nº de série</th>
                <th>Situação</th>
                <th>Voltou em</th>
                <th>Recebido por</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {ordenados.map((i) => (
                <tr key={i.id}>
                  <td>
                    <strong>{i.codigo}</strong>
                  </td>
                  <td>
                    {i.equipamento_nome || "—"}
                    {i.modelo && <span className="muted"> · {i.modelo}</span>}
                  </td>
                  <td>{i.numero_serie || "—"}</td>
                  <td>
                    {i.retornado_em ? (
                      <span className="pill pill-ok">Devolvido</span>
                    ) : i.estoque_situacao === "Faltante" ? (
                      <span className="pill pill-warn">Faltante</span>
                    ) : encerrada ? (
                      <span className={`pill ${tomDaSituacao(i.estoque_situacao)}`}>
                        {i.estoque_situacao || "—"}
                      </span>
                    ) : (
                      <span className="pill pill-neutral">Ainda fora</span>
                    )}
                    <Alertas alertas={i.alertas} />
                  </td>
                  <td>{i.retornado_em ? dataHora(i.retornado_em) : "—"}</td>
                  <td>
                    {i.retornado_por || "—"}
                    {i.retorno_origem === "camera" && <span className="conf-tag-camera">câmera</span>}
                  </td>
                  <td className="col-actions">
                    {/* Voltou da calibração: o certificado novo entra aqui. */}
                    {daCalibracao && i.retornado_em && i.equipamento_id != null && (
                      comCertNovo.has(i.equipamento_id) ? (
                        <span className="muted">
                          <IconCheck size={14} /> certificado anexado
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnexando({
                              id: Number(i.equipamento_id),
                              nome: i.equipamento_nome || i.codigo,
                            });
                          }}
                        >
                          <IconFile size={14} /> Anexar certificado novo
                        </button>
                      )
                    )}
                    {/* Desfazer só enquanto ele segue parado no estoque; o
                        servidor recusa se já saiu de novo. Numa saída
                        encerrada: só o ex-faltante (voltou DEPOIS do
                        encerramento — volta a constar como faltante), ou
                        qualquer um se ela fechou sem falta nenhuma. */}
                    {i.retornado_em &&
                      i.estoque_situacao === "Em estoque" &&
                      (!encerrada || exFaltante(i) || (dados.pendentes === 0 && !teveFaltante)) && (
                        <button
                          className="icon-btn icon-btn-danger"
                          title="Tirar da lista (desfazer esta devolução)"
                          data-grava
                          onClick={(e) => {
                            e.stopPropagation();
                            void desfazerDevolucao(i);
                          }}
                        >
                          <IconTrash size={16} />
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="ser-rodape">
        {!encerrada && (
          <button className="btn-secondary" disabled={ocupado} onClick={() => void encerrar()}>
            <IconCheck size={16} />{" "}
            {dados.pendentes > 0
              ? `Finalizar devolução (${dados.pendentes} não voltaram)`
              : "Encerrar esta saída"}
          </button>
        )}
        <button className="btn-secondary" onClick={onFechar}>
          Voltar para a lista
        </button>
      </div>
    </div>
  );
}
